import React, { useState, useEffect, useMemo } from 'react';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { auth, db } from './firebase';
import { useNavigate } from 'react-router-dom';
import { format, parseISO, isValid, subMonths, startOfMonth, endOfMonth, differenceInDays, differenceInMonths, addDays, compareAsc } from 'date-fns';
import { safeFormatDate } from './TradeForm';
import {
  Box,
  Button,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  alpha,
  useTheme,
  Alert,
  Divider,
  Tooltip,
  Badge,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Avatar,
  Autocomplete,
  TextField,
  Checkbox,
  Collapse,
  InputAdornment,
  Slider

} from '@mui/material';
import CalculateIcon from '@mui/icons-material/Calculate';

// Import Timeline components from @mui/lab instead of @mui/material
import {
  Timeline,
  TimelineItem,
  TimelineOppositeContent,
  TimelineSeparator,
  TimelineConnector,
  TimelineDot,
  TimelineContent
} from '@mui/lab';

import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip,
  Legend, ResponsiveContainer, Cell, Area, ComposedChart, Scatter, RadarChart, Radar, PolarGrid,
  PolarAngleAxis, PolarRadiusAxis, Treemap, AreaChart
} from 'recharts';

// Material Icons
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CompareIcon from '@mui/icons-material/Compare';
import TrackChangesIcon from '@mui/icons-material/TrackChanges';
import WarningIcon from '@mui/icons-material/Warning';
import InfoIcon from '@mui/icons-material/Info';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import TimelineIcon from '@mui/icons-material/Timeline';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import BarChartIcon from '@mui/icons-material/BarChart';
import PieChartIcon from '@mui/icons-material/PieChart';
import BubbleChartIcon from '@mui/icons-material/BubbleChart';
import DonutLargeIcon from '@mui/icons-material/DonutLarge';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import FilterListIcon from '@mui/icons-material/FilterList';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import EqualizerIcon from '@mui/icons-material/Equalizer';
import EventIcon from '@mui/icons-material/Event';
import ErrorIcon from '@mui/icons-material/Error';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import DateRangeIcon from '@mui/icons-material/DateRange';
import RepeatIcon from '@mui/icons-material/Repeat';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import HistoryIcon from '@mui/icons-material/History';
import WhatshotIcon from '@mui/icons-material/Whatshot';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import PeopleIcon from '@mui/icons-material/People';
import PersonIcon from '@mui/icons-material/Person';
import EmailIcon from '@mui/icons-material/Email';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';

// Colores para los gráficos
const COLORS = [
  '#8884d8', '#82ca9d', '#ffc658', '#ff8042', '#0088FE', '#00C49F',
  '#FFBB28', '#FF8042', '#a4de6c', '#d0ed57', '#83a6ed', '#8dd1e1'
];

// פונקציה לעיבוד רשימת ה-TP Milestones
// ממלאת אוטומטית את כל נקודות הביניים במרווחים של 0.5
const fillMissingMilestones = (milestones) => {
  if (!milestones || !Array.isArray(milestones) || milestones.length === 0) {
    return [];
  }
  
  // מצא את נקודת ה-TP הגבוהה ביותר
  let highestValue = 0;
  for (const milestone of milestones) {
    if (milestone && milestone.includes(':')) {
      const parts = milestone.split(':');
      if (parts.length === 2) {
        const value = parseFloat(parts[1]);
        if (!isNaN(value) && value > highestValue) {
          highestValue = value;
        }
      }
    }
  }
  
  // אם לא נמצאה נקודת TP תקינה, החזר את המערך המקורי
  if (highestValue <= 0) {
    return milestones;
  }
  
  // יצירת סט עם כל הנקודות הקיימות + החסרות
  const completeSet = new Set(milestones);
  
  // הוסף את כל נקודות הביניים בצעדים של 0.5
  for (let i = 0.5; i <= highestValue; i += 0.5) {
    // עיגול לדיוק של ספרה אחת אחרי הנקודה למניעת בעיות דיוק בנקודה צפה
    const roundedValue = Math.round(i * 10) / 10;
    const intermediateMS = `1:${roundedValue}`;
    completeSet.add(intermediateMS);
  }
  
  // המר בחזרה למערך וסדר לפי ערך מספרי
  return Array.from(completeSet).sort((a, b) => {
    const valueA = a.split(':').length === 2 ? parseFloat(a.split(':')[1]) : 0;
    const valueB = b.split(':').length === 2 ? parseFloat(b.split(':')[1]) : 0;
    return valueA - valueB;
  });
};

// Función para formatear la duración
const formatDuration = (minutes) => {
  if (!minutes || isNaN(minutes)) return '0 דקות';
  
  const days = Math.floor(minutes / (24 * 60));
  const hours = Math.floor((minutes % (24 * 60)) / 60);
  const mins = Math.floor(minutes % 60);
  
  let result = '';
  if (days > 0) result += `${days} י' `;
  if (hours > 0) result += `${hours} ש' `;
  if (mins > 0 || (days === 0 && hours === 0)) result += `${mins} ד'`;
  
  return result.trim();
};

// פונקציה למציאת רצפי ניצחונות/הפסדים
const findStreaks = (trades) => {
  if (!trades || trades.length === 0) return { winStreaks: [], lossStreaks: [] };
  
  // מיון העסקאות לפי תאריך
  const sortedTrades = [...trades].sort((a, b) => 
    isValid(a.parsedDate) && isValid(b.parsedDate) ? 
      compareAsc(a.parsedDate, b.parsedDate) : 0
  );
  
  const winStreaks = [];
  const lossStreaks = [];
  
  let currentWinStreak = [];
  let currentLossStreak = [];
  
  // חישוב הרצפים
  sortedTrades.forEach(trade => {
    if (trade.outcome === 'Take Profit') {
      // אם התחלנו רצף הפסדים, שמור אותו ואפס
      if (currentLossStreak.length > 0) {
        lossStreaks.push([...currentLossStreak]);
        currentLossStreak = [];
      }
      // הוסף לרצף הניצחונות הנוכחי
      currentWinStreak.push(trade);
    } else if (trade.outcome === 'Stop Loss') {
      // אם התחלנו רצף ניצחונות, שמור אותו ואפס
      if (currentWinStreak.length > 0) {
        winStreaks.push([...currentWinStreak]);
        currentWinStreak = [];
      }
      // הוסף לרצף ההפסדים הנוכחי
      currentLossStreak.push(trade);
    }
  });
  
  // בדוק אם סיימנו עם רצף פתוח
  if (currentWinStreak.length > 0) {
    winStreaks.push(currentWinStreak);
  }
  if (currentLossStreak.length > 0) {
    lossStreaks.push(currentLossStreak);
  }
  
  // מיון הרצפים לפי אורך (מהארוך לקצר)
  winStreaks.sort((a, b) => b.length - a.length);
  lossStreaks.sort((a, b) => b.length - a.length);
  
  return { winStreaks, lossStreaks };
};

// פונקציה לחישוב תקופות המסחר
const calculateTradingPeriods = (trades) => {
  if (!trades || trades.length === 0) return null;
  
  // מיון העסקאות לפי תאריך
  const validDates = trades
    .filter(trade => isValid(trade.parsedDate))
    .map(trade => trade.parsedDate);
  
  if (validDates.length === 0) return null;
  
  // מציאת התאריך המוקדם והמאוחר ביותר
  const firstDate = validDates.reduce((earliest, date) => 
    compareAsc(date, earliest) < 0 ? date : earliest, validDates[0]);
  
  const lastDate = validDates.reduce((latest, date) => 
    compareAsc(date, latest) > 0 ? date : latest, validDates[0]);
  
  // חישוב הבדלי זמנים
  const daysDiff = differenceInDays(lastDate, firstDate);
  const monthsDiff = differenceInMonths(lastDate, firstDate);
  
  // יצירת מערך של כל החודשים בתקופה
  const months = [];
  let currentDate = startOfMonth(firstDate);
  const lastMonth = startOfMonth(lastDate);
  
  while (compareAsc(currentDate, lastMonth) <= 0) {
    months.push(safeFormatDate(currentDate, 'yyyy-MM'));
    currentDate = addDays(endOfMonth(currentDate), 1);
  }
  
  return {
    firstDate,
    lastDate,
    daysDiff,
    monthsDiff,
    months,
    monthsArray: months.map(month => {
      const [year, monthNum] = month.split('-');
      return {
        month,
        name: safeFormatDate(new Date(parseInt(year), parseInt(monthNum) - 1, 1), 'MMM yyyy')
      };
    })
  };
};

const TPExitSimulator = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const currentUser = auth.currentUser;

  // Estados principales
  const [trades, setTrades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(0);
  const [selectedMilestone, setSelectedMilestone] = useState('all');
  const [availableMilestones, setAvailableMilestones] = useState(['all']);
  const [timeFilter, setTimeFilter] = useState('all');
  const [symbolFilter, setSymbolFilter] = useState('all');
  const [detailTrade, setDetailTrade] = useState(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [alertSeverity, setAlertSeverity] = useState('info');
  const [alertOpen, setAlertOpen] = useState(false);
  const [simulationResults, setSimulationResults] = useState({});
  const [optimalExitPoint, setOptimalExitPoint] = useState(null);
  const [simulatedStreakData, setSimulatedStreakData] = useState({ winStreaks: [], lossStreaks: [] });
  const [calculateOptimalRisk, setCalculateOptimalRisk] = useState({
    riskPerTrade: 0,
    totalPortfolioPercentage: 0,
    isValid: false
  });
    // להוסיף ליד שאר ה-useState:
const [maxDrawdownInput, setMaxDrawdownInput] = useState(20); // ברירת מחדל 20%
  // Estados de filtrado avanzado
  const [sessionFilter, setSessionFilter] = useState('all');
  const [availableSessions, setAvailableSessions] = useState(['all']);
  const [dayFilter, setDayFilter] = useState('all');
  const [availableDays, setAvailableDays] = useState(['all']);

  // נתוני תקופות ורצפים
  const [tradingPeriods, setTradingPeriods] = useState(null);
  const [streakData, setStreakData] = useState({ winStreaks: [], lossStreaks: [] });

  // מצב בחירת משתמשים
  const [allUsers, setAllUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [userSelectorOpen, setUserSelectorOpen] = useState(false);

  // Obtener usuarios disponibles y sus emails
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        // שימוש בקולקציית users ישירות כמו ב-Stats.js
        const usersRef = collection(db, 'users');
        const usersSnapshot = await getDocs(usersRef);
        const usersData = [];
        
        usersSnapshot.forEach((doc) => {
          const userData = doc.data();
          usersData.push({
            uid: doc.id,
            email: userData.email || 'אימייל לא זמין',
            displayName: userData.userName || (userData.email ? userData.email.split('@')[0] : 'משתמש לא ידוע')
          });
        });
        
        setAllUsers(usersData);
        
        if (currentUser) {
          const defaultUser = usersData.find(user => user.uid === currentUser.uid);
          if (defaultUser) {
            setSelectedUsers([defaultUser]);
          } else if (usersData.length > 0) {
            // אם המשתמש הנוכחי לא נמצא, בחר את המשתמש הראשון ברשימה
            setSelectedUsers([usersData[0]]);
          }
        } else if (usersData.length > 0) {
          // אם אין משתמש נוכחי, בחר את המשתמש הראשון ברשימה
          setSelectedUsers([usersData[0]]);
        }
        
        
      } catch (error) {
        console.error("Error fetching users:", error);
        setAlertMessage('שגיאה בטעינת רשימת המשתמשים');
        setAlertSeverity('error');
        setAlertOpen(true);
      }
    };
    
    fetchUsers();
  }, [currentUser]);

  // Obtener datos de las operaciones desde Firebase basado en usuarios seleccionados
  useEffect(() => {
    const fetchTrades = async () => {
      try {
        setLoading(true);
        
        console.log("Selected users for fetching trades:", selectedUsers);
        
        if (selectedUsers.length === 0) {
          console.log("No users selected, returning empty trades array");
          setTrades([]);
          setLoading(false);
          return;
        }
            
        const selectedUserIds = selectedUsers.map(user => user.uid);
        const tradesData = [];
        
        // בצע שאילתא נפרדת לכל משתמש ואחד את התוצאות
        for (const userId of selectedUserIds) {
          const tradesRef = collection(db, 'trades');
          const q = query(tradesRef, where('uid', '==', userId));
          
          const querySnapshot = await getDocs(q);
          
          querySnapshot.forEach((doc) => {
            const data = doc.data();
            
            // שמירת הנתונים הבסיסיים
            const tradeData = {
              id: doc.id,
              ...data,
              parsedDate: data.date ? parseISO(data.date) : new Date(),
              // וידוא שהמערך קיים
              tpMilestonesReached: data.tpMilestonesReached || [],
              // הוסף אימייל המשתמש לנתונים
              userEmail: data.email || 'Unknown'
            };
            
            // מילוי נקודות TP חסרות
            tradeData.tpMilestonesReached = fillMissingMilestones(tradeData.tpMilestonesReached);
            
            tradesData.push(tradeData);
          });
        }
        
        console.log("Operaciones obtenidas:", tradesData.length);
        console.log("Operaciones con TRIGGER:", tradesData.filter(t => t.triggerAppeared === 'yes').length);
        setTrades(tradesData);
        
        // חישוב נתוני תקופות ורצפים
        setTradingPeriods(calculateTradingPeriods(tradesData));
        setStreakData(findStreaks(tradesData.filter(t => t.triggerAppeared === 'yes')));
        
        // Recopilar todos los valores de TP Milestone disponibles
        const allMilestones = new Set();
        const allSessions = new Set(['all']);
        const allDays = new Set(['all']);
        
        tradesData.forEach(trade => {
          // Recopilar milestones
          if (trade.tpMilestonesReached && Array.isArray(trade.tpMilestonesReached)) {
            trade.tpMilestonesReached.forEach(milestone => {
              if (milestone) {
                allMilestones.add(milestone);
              }
            });
          }
          
          // Recopilar sesiones
          if (trade.session) {
            allSessions.add(trade.session);
          }
          
          // Recopilar días de la semana
          if (trade.dayOfWeek) {
            allDays.add(trade.dayOfWeek);
          }
        });
        
        // Ordenar los milestones por valor numérico (1:1, 1:2, etc.)
        const sortedMilestones = Array.from(allMilestones)
          .sort((a, b) => {
            const valueA = a.split(':').length === 2 ? parseFloat(a.split(':')[1]) : 0;
            const valueB = b.split(':').length === 2 ? parseFloat(b.split(':')[1]) : 0;
            return valueA - valueB;
          });
        
        setAvailableMilestones(['all', ...sortedMilestones]);
        setAvailableSessions(Array.from(allSessions));
        setAvailableDays(Array.from(allDays));
        
        if (sortedMilestones.length > 0) {
          setSelectedMilestone(sortedMilestones[0]);
        }
        
      } catch (error) {
        console.error("Error al obtener operaciones:", error);
        setAlertMessage('שגיאה בטעינת נתוני העסקאות');
        setAlertSeverity('error');
        setAlertOpen(true);
        setTrades([]);
      } finally {
        setLoading(false);
      }
    };
    
    fetchTrades();
  }, [selectedUsers]);

  // Realizar simulación de rendimiento para cada milestone
  useEffect(() => {
    if (trades.length === 0 || !availableMilestones || availableMilestones.length <= 1) {
      return;
    }

    // Filtrar solo operaciones con TRIGGER
    const tradesWithTrigger = trades.filter(trade => trade.triggerAppeared === 'yes');
    
    if (tradesWithTrigger.length === 0) {
      setSimulationResults({});
      setOptimalExitPoint(null);
      return;
    }
    
    // Crear informe de rendimiento simulado para cada milestone
    const allSimulations = {};
    
    availableMilestones.forEach(milestone => {
      if (milestone === 'all') return;
      
      // Obtener el valor R del milestone
      const targetR = milestone.split(':').length === 2 ? parseFloat(milestone.split(':')[1]) : 1;
      
      // Simular operaciones con este punto de salida
      const simulatedTrades = tradesWithTrigger.map(trade => {
        // Comprobar si la operación alcanzó este milestone
        const reachedMilestone = trade.tpMilestonesReached && 
                                Array.isArray(trade.tpMilestonesReached) && 
                                trade.tpMilestonesReached.includes(milestone);
        
        // Crear operación simulada
        return {
          ...trade,
          simulatedOutcome: reachedMilestone ? 'Take Profit' : 'Stop Loss',
          simulatedR: reachedMilestone ? targetR : -1,
          reachedMilestone
        };
      });
      
      // Calcular estadísticas
      const winCount = simulatedTrades.filter(t => t.simulatedOutcome === 'Take Profit').length;
      const lossCount = simulatedTrades.filter(t => t.simulatedOutcome === 'Stop Loss').length;
      const totalTrades = simulatedTrades.length;
      const winRate = totalTrades > 0 ? (winCount / totalTrades) * 100 : 0;
      
      // Calcular valor R total
      const totalR = simulatedTrades.reduce((acc, trade) => acc + trade.simulatedR, 0);
      const avgR = totalTrades > 0 ? totalR / totalTrades : 0;
      
      // Calcular factor de beneficio y expectativa
      const grossProfit = simulatedTrades
        .filter(t => t.simulatedR > 0)
        .reduce((acc, t) => acc + t.simulatedR, 0);
      const grossLoss = Math.abs(simulatedTrades
        .filter(t => t.simulatedR < 0)
        .reduce((acc, t) => acc + t.simulatedR, 0));
      const profitFactor = grossLoss > 0 ? grossProfit / grossLoss : grossProfit > 0 ? grossProfit : 0;
      
      const expectancy = winCount > 0 && lossCount > 0 ? 
        (winRate / 100) * (grossProfit / winCount) - ((100 - winRate) / 100) * (grossLoss / lossCount) : 0;
      
      // Analizar cambios en operaciones comparado con resultados reales
      const improvedTrades = simulatedTrades.filter(t => 
        t.outcome === 'Stop Loss' && t.simulatedOutcome === 'Take Profit'
      );
      
      const worseTrades = simulatedTrades.filter(t => 
        t.outcome === 'Take Profit' && t.simulatedOutcome === 'Stop Loss'
      );
      
      const unchangedTrades = simulatedTrades.filter(t => 
        (t.outcome === 'Take Profit' && t.simulatedOutcome === 'Take Profit') ||
        (t.outcome === 'Stop Loss' && t.simulatedOutcome === 'Stop Loss')
      );
      
      // Identificar "oportunidades perdidas" - operaciones que alcanzaron el milestone pero terminaron en pérdida
      const missedOpportunities = simulatedTrades.filter(t => 
        t.reachedMilestone && t.outcome === 'Stop Loss'
      );
      
      // Análisis por símbolo
      const symbolAnalysis = {};
      simulatedTrades.forEach(trade => {
        const symbol = trade.symbol || 'Desconocido';
        if (!symbolAnalysis[symbol]) {
          symbolAnalysis[symbol] = {
            symbol,
            totalTrades: 0,
            winCount: 0,
            lossCount: 0,
            totalR: 0
          };
        }
        
        symbolAnalysis[symbol].totalTrades++;
        if (trade.simulatedOutcome === 'Take Profit') {
          symbolAnalysis[symbol].winCount++;
          symbolAnalysis[symbol].totalR += trade.simulatedR;
        } else {
          symbolAnalysis[symbol].lossCount++;
          symbolAnalysis[symbol].totalR += trade.simulatedR;
        }
      });
      
      // Análisis por sesión
      const sessionAnalysis = {};
      simulatedTrades.forEach(trade => {
        const session = trade.session || 'Desconocido';
        if (!sessionAnalysis[session]) {
          sessionAnalysis[session] = {
            session,
            totalTrades: 0,
            winCount: 0,
            lossCount: 0,
            totalR: 0
          };
        }
        
        sessionAnalysis[session].totalTrades++;
        if (trade.simulatedOutcome === 'Take Profit') {
          sessionAnalysis[session].winCount++;
          sessionAnalysis[session].totalR += trade.simulatedR;
        } else {
          sessionAnalysis[session].lossCount++;
          sessionAnalysis[session].totalR += trade.simulatedR;
        }
      });
      
      // Análisis por día de la semana
      const dayAnalysis = {};
      simulatedTrades.forEach(trade => {
        const day = trade.dayOfWeek || 'Desconocido';
        if (!dayAnalysis[day]) {
          dayAnalysis[day] = {
            day,
            totalTrades: 0,
            winCount: 0,
            lossCount: 0,
            totalR: 0
          };
        }
        
        dayAnalysis[day].totalTrades++;
        if (trade.simulatedOutcome === 'Take Profit') {
          dayAnalysis[day].winCount++;
          dayAnalysis[day].totalR += trade.simulatedR;
        } else {
          dayAnalysis[day].lossCount++;
          dayAnalysis[day].totalR += trade.simulatedR;
        }
      });

      // Análisis por usuario
      const userAnalysis = {};
      simulatedTrades.forEach(trade => {
        const userEmail = trade.userEmail || 'Unknown';
        if (!userAnalysis[userEmail]) {
          userAnalysis[userEmail] = {
            userEmail,
            totalTrades: 0,
            winCount: 0,
            lossCount: 0,
            totalR: 0
          };
        }
        
        userAnalysis[userEmail].totalTrades++;
        if (trade.simulatedOutcome === 'Take Profit') {
          userAnalysis[userEmail].winCount++;
          userAnalysis[userEmail].totalR += trade.simulatedR;
        } else {
          userAnalysis[userEmail].lossCount++;
          userAnalysis[userEmail].totalR += trade.simulatedR;
        }
      });
      
      // Análisis mensual
      const monthlyPerformance = {};
      simulatedTrades.forEach(trade => {
        if (!trade.parsedDate || !isValid(trade.parsedDate)) return;
        
        const yearMonth = safeFormatDate(trade.parsedDate, 'yyyy-MM');
        if (!monthlyPerformance[yearMonth]) {
          monthlyPerformance[yearMonth] = {
            month: safeFormatDate(trade.parsedDate, 'MMM yyyy'),
            totalTrades: 0,
            winCount: 0,
            lossCount: 0,
            totalR: 0
          };
        }
        
        monthlyPerformance[yearMonth].totalTrades++;
        if (trade.simulatedOutcome === 'Take Profit') {
          monthlyPerformance[yearMonth].winCount++;
          monthlyPerformance[yearMonth].totalR += trade.simulatedR;
        } else {
          monthlyPerformance[yearMonth].lossCount++;
          monthlyPerformance[yearMonth].totalR += trade.simulatedR;
        }
      });
      
      // Calcular puntuación general para determinar la mejor estrategia
      // Fórmula: (totalR * 0.4) + (winRate * 0.3) + (profitFactor * 10)
      const score = (totalR * 0.4) + (winRate * 0.3) + (profitFactor * 10);
      
      // Guardar resultados de simulación
      allSimulations[milestone] = {
        milestone,
        targetR,
        totalTrades,
        winCount,
        lossCount,
        winRate,
        totalR,
        avgR,
        profitFactor,
        expectancy,
        improvedTrades,
        worseTrades,
        unchangedTrades,
        missedOpportunities,
        simulatedTrades,
        symbolAnalysis: Object.values(symbolAnalysis).map(s => ({
          ...s,
          winRate: s.totalTrades > 0 ? (s.winCount / s.totalTrades) * 100 : 0
        })),
        sessionAnalysis: Object.values(sessionAnalysis).map(s => ({
          ...s,
          winRate: s.totalTrades > 0 ? (s.winCount / s.totalTrades) * 100 : 0
        })),
        dayAnalysis: Object.values(dayAnalysis).map(d => ({
          ...d,
          winRate: d.totalTrades > 0 ? (d.winCount / d.totalTrades) * 100 : 0
        })),
        userAnalysis: Object.values(userAnalysis).map(u => ({
          ...u,
          winRate: u.totalTrades > 0 ? (u.winCount / u.totalTrades) * 100 : 0
        })),
        monthlyPerformance: Object.values(monthlyPerformance)
          .map(m => ({
            ...m,
            winRate: m.totalTrades > 0 ? (m.winCount / m.totalTrades) * 100 : 0
          }))
          .sort((a, b) => {
            const monthA = a.month.split(' ');
            const monthB = b.month.split(' ');
            if (monthA[1] !== monthB[1]) return monthA[1] - monthB[1];
            return monthA[0].localeCompare(monthB[0]);
          }),
        score
      };
    });
    
    setSimulationResults(allSimulations);
    
    // Identificar el punto de salida óptimo basado en la puntuación
    if (Object.keys(allSimulations).length > 0) {
      const sortedByScore = Object.values(allSimulations).sort((a, b) => b.score - a.score);
      setOptimalExitPoint(sortedByScore[0]?.milestone || null);
    }
    
  }, [trades, availableMilestones]);
// חישוב רצפים לפי תוצאות הסימולציה
useEffect(() => {
  if (selectedMilestone === 'all' || !simulationResults[selectedMilestone]) {
    return;
  }
  // חישוב אחוז סיכון אופטימלי
  const simulatedTrades = simulationResults[selectedMilestone].simulatedTrades;
  
  // עדכן את מבנה העסקאות כך שנשתמש ב-simulatedOutcome במקום outcome המקורי
  const tradesForStreakCalculation = simulatedTrades.map(trade => ({
    ...trade,
    outcome: trade.simulatedOutcome // השתמש בתוצאה המסומלצת
  }));
  
  // חשב רצפים חדשים
  const newStreakData = findStreaks(tradesForStreakCalculation);
  setSimulatedStreakData(newStreakData);
  
}, [selectedMilestone, simulationResults]);

// הוסף ישירות אחריו (ברמה העליונה של הקומפוננט, לא בתוך callback):
useEffect(() => {
  if (selectedMilestone === 'all' || !simulationResults[selectedMilestone]) {
    return;
  }
  
  // חשב רצפים חדשים לפי תוצאות הסימולציה
  const simulatedTrades = simulationResults[selectedMilestone].simulatedTrades;
  
  // עדכן את מבנה העסקאות כך שנשתמש ב-simulatedOutcome במקום outcome המקורי
  const tradesForStreakCalculation = simulatedTrades.map(trade => ({
    ...trade,
    outcome: trade.simulatedOutcome // השתמש בתוצאה המסומלצת
  }));
  
  // חשב רצפים חדשים
  const newStreakData = findStreaks(tradesForStreakCalculation);
  setSimulatedStreakData(newStreakData);
  
}, [selectedMilestone, simulationResults]);

// הוסף את הuseEffect החדש כאן:
useEffect(() => {
  if (selectedMilestone === 'all' || !simulationResults[selectedMilestone]) {
    setCalculateOptimalRisk({
      riskPerTrade: 0,
      totalPortfolioPercentage: 0,
      isValid: false
    });
    return;
  }
  
  const longestLossStreak = simulatedStreakData.lossStreaks[0]?.length || 0;
  if (longestLossStreak <= 0) return;
  
  // השתמש בערך שהמשתמש הזין
  const maxDrawdown = maxDrawdownInput; 
  const cumulativeR = simulationResults[selectedMilestone]?.totalR || 0;
  
  const riskPerTrade = maxDrawdown / longestLossStreak;
  const totalPortfolioPercentage = riskPerTrade * cumulativeR;
  
  setCalculateOptimalRisk({
    riskPerTrade,
    totalPortfolioPercentage,
    isValid: true
  });
}, [selectedMilestone, simulatedStreakData, simulationResults, maxDrawdownInput]);

  // Filtrar operaciones según los filtros aplicados
  const filteredTrades = useMemo(() => {
    // Primero filtrar solo operaciones con TRIGGER
    let filtered = trades.filter(trade => trade.triggerAppeared === 'yes');
    
    // Aplicar filtro de tiempo
    if (timeFilter === 'last30') {
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      filtered = filtered.filter(trade => isValid(trade.parsedDate) && trade.parsedDate >= thirtyDaysAgo);
    } else if (timeFilter === 'last90') {
      const ninetyDaysAgo = new Date();
      ninetyDaysAgo.setDate(ninetyDaysAgo.getDate() - 90);
      filtered = filtered.filter(trade => isValid(trade.parsedDate) && trade.parsedDate >= ninetyDaysAgo);
    } else if (timeFilter === 'thisYear') {
      const startOfYear = new Date(new Date().getFullYear(), 0, 1);
      filtered = filtered.filter(trade => isValid(trade.parsedDate) && trade.parsedDate >= startOfYear);
    }
    
    // Aplicar filtro de símbolo
    if (symbolFilter !== 'all') {
      filtered = filtered.filter(trade => trade.symbol === symbolFilter);
    }
    
    // Aplicar filtro de sesión
    if (sessionFilter !== 'all') {
      filtered = filtered.filter(trade => trade.session === sessionFilter);
    }
    
    // Aplicar filtro de día
    if (dayFilter !== 'all') {
      filtered = filtered.filter(trade => trade.dayOfWeek === dayFilter);
    }
    
    return filtered;
  }, [trades, timeFilter, symbolFilter, sessionFilter, dayFilter]);

  // Obtener simulación para el milestone seleccionado
  const selectedSimulation = useMemo(() => {
    if (selectedMilestone === 'all' || !simulationResults[selectedMilestone]) {
      return null;
    }
    
    return simulationResults[selectedMilestone];
  }, [selectedMilestone, simulationResults]);

  // Calcular estadísticas base (resultados reales)
  const baselineStats = useMemo(() => {
    if (filteredTrades.length === 0) {
      return {
        totalTrades: 0,
        tpCount: 0,
        slCount: 0,
        winRate: 0,
        totalR: 0,
        avgR: 0,
        profitFactor: 0
      };
    }
    
    const tpCount = filteredTrades.filter(t => t.outcome === 'Take Profit').length;
    const slCount = filteredTrades.filter(t => t.outcome === 'Stop Loss').length;
    const winRate = (tpCount / filteredTrades.length) * 100;
    
    // Calcular valor R total actual
    let totalR = 0;
    let totalWin = 0;
    let totalLoss = 0;
    
    filteredTrades.forEach(trade => {
      if (trade.outcome === 'Take Profit') {
        let r = 1; // Valor por defecto
        if (trade.tpMilestoneExit) {
          const parts = trade.tpMilestoneExit.split(':');
          if (parts.length === 2) {
            r = parseFloat(parts[1]);
          }
        }
        totalR += r;
        totalWin += r;
      } else if (trade.outcome === 'Stop Loss') {
        totalR -= 1;
        totalLoss += 1;
      }
    });
    
    const avgR = totalR / filteredTrades.length;
    const profitFactor = totalLoss > 0 ? totalWin / totalLoss : totalWin > 0 ? totalWin : 0;
    
    // Add analysis per user
    const userStats = {};
    filteredTrades.forEach(trade => {
      const userEmail = trade.userEmail || 'Unknown';
      if (!userStats[userEmail]) {
        userStats[userEmail] = {
          totalTrades: 0,
          tpCount: 0,
          slCount: 0,
          totalR: 0
        };
      }

      userStats[userEmail].totalTrades++;
      
      if (trade.outcome === 'Take Profit') {
        userStats[userEmail].tpCount++;
        let r = 1;
        if (trade.tpMilestoneExit) {
          const parts = trade.tpMilestoneExit.split(':');
          if (parts.length === 2) {
            r = parseFloat(parts[1]);
          }
        }
        userStats[userEmail].totalR += r;
      } else if (trade.outcome === 'Stop Loss') {
        userStats[userEmail].slCount++;
        userStats[userEmail].totalR -= 1;
      }
    });

    // Calculate win rates and averages for each user
    const userAnalysis = Object.entries(userStats).map(([userEmail, stats]) => ({
      userEmail,
      ...stats,
      winRate: stats.totalTrades > 0 ? (stats.tpCount / stats.totalTrades) * 100 : 0,
      avgR: stats.totalTrades > 0 ? stats.totalR / stats.totalTrades : 0
    }));
    
    return {
      totalTrades: filteredTrades.length,
      tpCount,
      slCount,
      winRate,
      totalR,
      avgR,
      profitFactor,
      userAnalysis
    };
  }, [filteredTrades]);

  // Símbolos disponibles para filtrar
  const availableSymbols = useMemo(() => {
    const symbols = new Set();
    trades.forEach(trade => {
      if (trade.symbol) {
        symbols.add(trade.symbol);
      }
    });
    return ['all', ...Array.from(symbols)];
  }, [trades]);

  // Datos para comparación de todos los milestones
  const comparisonData = useMemo(() => {
    return Object.values(simulationResults)
      .sort((a, b) => a.targetR - b.targetR)
      .map(sim => ({
        name: sim.milestone,
        totalR: sim.totalR,
        avgR: sim.avgR,
        winRate: sim.winRate,
        profitFactor: sim.profitFactor,
        expectancy: sim.expectancy,
        improvedCount: sim.improvedTrades.length,
        worseCount: sim.worseTrades.length,
        missedCount: sim.missedOpportunities.length,
        score: sim.score
      }));
  }, [simulationResults]);

  // Manejadores de eventos
  const handleMilestoneChange = (event) => {
    setSelectedMilestone(event.target.value);
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleDetailClick = (trade) => {
    setDetailTrade(trade);
    setDetailDialogOpen(true);
  };

  const handleBack = () => {
    navigate('/UTSP');
  };

  const handleUserChange = (event, newValue) => {
    setSelectedUsers(newValue);
  };

  const handleToggleUserSelector = () => {
    setUserSelectorOpen(!userSelectorOpen);
  };

  // Componente personalizado de Tooltip para Recharts
  const CustomTooltip = ({ active, payload, label, valuePrefix, valueSuffix }) => {
    if (active && payload && payload.length) {
      return (
        <Paper sx={{ p: 1, bgcolor: 'rgba(255, 255, 255, 0.95)', boxShadow: theme.shadows[3] }}>
          <Typography variant="subtitle2">{label}</Typography>
          {payload.map((entry, index) => (
            <Box key={`tooltip-${index}`} sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
              <Box
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: '50%',
                  backgroundColor: entry.color,
                  mr: 1
                }}
              />
              <Typography variant="body2" color="text.secondary">
                {entry.name}: {valuePrefix || ''}{typeof entry.value === 'number' ? entry.value.toFixed(2) : entry.value}{valueSuffix || ''}
              </Typography>
            </Box>
          ))}
        </Paper>
      );
    }
    return null;
  };

  // Estilos
  const styles = {
    root: {
      backgroundColor: alpha(theme.palette.primary.main, 0.05),
      minHeight: '100vh',
    },
    mainContent: {
      p: 3,
      direction: 'rtl',
      minHeight: 'calc(100vh - 64px)',
    },
    card: {
      borderRadius: 2,
      boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
      height: '100%',
    },
    header: {
      p: 2, 
      bgcolor: alpha(theme.palette.primary.main, 0.1),
      borderRadius: '4px 4px 0 0',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between'
    },
    filterBox: {
      display: 'flex',
      gap: 2,
      mb: 3,
      alignItems: 'center',
      flexWrap: 'wrap',
    },
    infoBox: {
      p: 2,
      borderRadius: 2,
      bgcolor: alpha(theme.palette.info.main, 0.1),
      border: `1px solid ${alpha(theme.palette.info.main, 0.3)}`,
      mb: 3
    },
    statCard: {
      p: 2,
      borderRadius: 2,
      bgcolor: 'background.paper',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      height: '100%',
      boxShadow: `0 0 10px ${alpha(theme.palette.primary.main, 0.1)}`,
      '&:hover': {
        boxShadow: `0 0 15px ${alpha(theme.palette.primary.main, 0.2)}`,
      }
    },
    tableContainer: {
      borderRadius: 2,
      overflow: 'hidden',
      boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
    },
    tableHeader: {
      backgroundColor: alpha(theme.palette.primary.main, 0.05),
    },
    tableHeaderCell: {
      fontWeight: 'bold',
      color: theme.palette.primary.main,
    },
    tabs: {
      bgcolor: alpha(theme.palette.background.paper, 0.6),
      borderRadius: 3,
      boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
      mb: 3,
      '& .MuiTabs-indicator': {
        height: 4,
        borderRadius: 2,
      },
      '& .MuiTab-root': {
        minHeight: 60,
        fontWeight: 'bold',
        '&.Mui-selected': {
          color: theme.palette.primary.main,
        },
      },
    },
    userSelector: {
      p: 2,
      mb: 3,
      borderRadius: 2,
      bgcolor: alpha(theme.palette.primary.main, 0.08),
      border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
    },
    userChip: {
      m: 0.5,
      '& .MuiChip-label': {
        maxWidth: 150,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      }
    }
  };

  // Selector de usuarios
  const UserSelector = () => (
    <Box sx={styles.userSelector}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <PeopleIcon sx={{ mr: 1 }} color="primary" />
          <Typography variant="h6" fontWeight="bold" color="primary.main">
            בחירת משתמשים לניתוח
          </Typography>
        </Box>
        <Button 
          variant="outlined" 
          size="small" 
          startIcon={userSelectorOpen ? <ArrowUpwardIcon /> : <ArrowDownwardIcon />}
          onClick={handleToggleUserSelector}
        >
          {userSelectorOpen ? 'הסתר' : 'הצג פרטים'}
        </Button>
      </Box>

      <Collapse in={userSelectorOpen}>
        <Autocomplete
          multiple
          id="user-selector"
          options={allUsers}
          value={selectedUsers}
          onChange={handleUserChange}
          getOptionLabel={(option) => option.email || ''}
          renderInput={(params) => (
            <TextField
              {...params}
              variant="outlined"
              label="בחר משתמשים"
              placeholder="חפש לפי אימייל"
              InputProps={{
                ...params.InputProps,
                startAdornment: (
                  <>
                    <EmailIcon color="action" sx={{ mr: 1 }} />
                    {params.InputProps.startAdornment}
                  </>
                )
              }}
            />
          )}
          renderOption={(props, option, { selected }) => (
            <li {...props}>
              <Checkbox
                icon={<CheckCircleIcon fontSize="small" />}
                checkedIcon={<CheckCircleIcon fontSize="small" />}
                style={{ marginRight: 8 }}
                checked={selected}
              />
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                <Typography variant="body2" fontWeight="bold">{option.email}</Typography>
                {option.displayName && option.displayName !== option.email.split('@')[0] && (
                  <Typography variant="caption" color="text.secondary">{option.displayName}</Typography>
                )}
              </Box>
            </li>
          )}
        />

        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2" gutterBottom>משתמשים נבחרים:</Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap' }}>
            {selectedUsers.length === 0 ? (
              <Typography variant="body2" color="text.secondary">לא נבחרו משתמשים</Typography>
            ) : (
              selectedUsers.map((user) => (
                <Chip
                  key={user.uid}
                  label={user.email}
                  onDelete={() => setSelectedUsers(selectedUsers.filter(u => u.uid !== user.uid))}
                  color="primary"
                  sx={styles.userChip}
                  icon={<PersonIcon />}
                />
              ))
            )}
          </Box>
        </Box>

        <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
          <Button
            variant="contained"
            color="primary"
            startIcon={<DoneAllIcon />}
            onClick={() => setSelectedUsers(allUsers)}
            size="small"
          >
            בחר הכל
          </Button>
          <Button
            variant="outlined"
            startIcon={<SwapHorizIcon />}
            onClick={() => {
              // Invert selection
              const unselectedUsers = allUsers.filter(user => 
                !selectedUsers.some(selected => selected.uid === user.uid)
              );
              setSelectedUsers(unselectedUsers);
            }}
            size="small"
          >
            הפוך בחירה
          </Button>
          <Button
            variant="outlined"
            color="error"
            onClick={() => setSelectedUsers([])}
            size="small"
          >
            נקה בחירה
          </Button>
        </Box>
      </Collapse>

      {!userSelectorOpen && selectedUsers.length > 0 && (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography variant="body2" sx={{ mr: 1 }}>
            נבחרו {selectedUsers.length} משתמשים:
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', flex: 1 }}>
            {selectedUsers.slice(0, 3).map(user => (
              <Chip
                key={user.uid}
                label={user.email}
                size="small"
                sx={styles.userChip}
              />
            ))}
            {selectedUsers.length > 3 && (
              <Chip
                label={`+${selectedUsers.length - 3} נוספים`}
                size="small"
                color="primary"
                variant="outlined"
                sx={styles.userChip}
              />
            )}
          </Box>
        </Box>
      )}
    </Box>
  );

  // Diálogo de detalle de operación
  const TradeDetailDialog = () => (
    <Dialog
      open={detailDialogOpen}
      onClose={() => setDetailDialogOpen(false)}
      maxWidth="md"
      fullWidth
    >
      {detailTrade && (
        <>
          <DialogTitle>
            פרטי עסקה: {detailTrade.symbol} {detailTrade.outcome === 'Take Profit' ? 
              <Chip label="Take Profit" color="success" size="small" /> : 
              <Chip label="Stop Loss" color="error" size="small" />}
          </DialogTitle>
          <DialogContent>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1" color="primary" gutterBottom>מידע בסיסי</Typography>
                <Box sx={{ mr: 2 }}>
                  <Typography variant="body2"><b>תאריך:</b> {detailTrade.date}</Typography>
                  <Typography variant="body2"><b>יום:</b> {detailTrade.dayOfWeek}</Typography>
                  <Typography variant="body2"><b>שעה:</b> {detailTrade.time}</Typography>
                  <Typography variant="body2"><b>סשן:</b> {detailTrade.session}</Typography>
                  <Typography variant="body2"><b>משתמש:</b> {detailTrade.userEmail}</Typography>
                  <Typography variant="body2"><b>טריגר תואם:</b> {detailTrade.matchedTrigger}</Typography>
                  <Typography variant="body2"><b>משך:</b> {formatDuration(detailTrade.duration)}</Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1" color="primary" gutterBottom>ביצועים</Typography>
                <Box sx={{ mr: 2 }}>
                  <Typography variant="body2"><b>אחוז סטופ:</b> {detailTrade.stopPercentage}</Typography>
                  <Typography variant="body2"><b>נקודת יציאה בפועל:</b> {detailTrade.tpMilestoneExit || 'Stop Loss'}</Typography>
                  {detailTrade.outcome === 'Stop Loss' && (
                    <Typography variant="body2"><b>סיבת סטופ:</b> {detailTrade.stopReason}</Typography>
                  )}
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle1" color="primary" gutterBottom>אבני דרך TP שהושגו</Typography>
                {detailTrade.tpMilestonesReached && detailTrade.tpMilestonesReached.length > 0 ? (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                    {detailTrade.tpMilestonesReached.map((milestone, idx) => (
                      <Chip 
                        key={idx}
                        label={milestone}
                        color="success"
                        icon={<CheckCircleIcon />}
                        variant="outlined"
                      />
                    ))}
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary">אין אבני דרך TP זמינות</Typography>
                )}
              </Grid>
              {selectedSimulation && (
                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle1" color="primary" gutterBottom>סימולציית יציאה ב-{selectedMilestone}</Typography>
                  <Box sx={{ bgcolor: alpha(theme.palette.primary.main, 0.05), p: 2, borderRadius: 1 }}>
                    <Typography variant="body2">
                      <b>האם העסקה הגיעה ל-{selectedMilestone}?</b>{' '}
                      {detailTrade.tpMilestonesReached && detailTrade.tpMilestonesReached.includes(selectedMilestone) ? (
                        <Chip label="כן" color="success" size="small" />
                      ) : (
                        <Chip label="לא" color="error" size="small" />
                      )}
                    </Typography>
                    
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      <b>תוצאה אילו היית יוצא ב-{selectedMilestone}:</b>{' '}
                      {detailTrade.tpMilestonesReached && detailTrade.tpMilestonesReached.includes(selectedMilestone) ? (
                        <>
                          <Chip label="Take Profit" color="success" size="small" />{' '}
                          <Typography component="span" fontWeight="bold" color="success.main">
                            +{selectedMilestone.split(':')[1]}R
                          </Typography>
                        </>
                      ) : (
                        <>
                          <Chip label="Stop Loss" color="error" size="small" />{' '}
                          <Typography component="span" fontWeight="bold" color="error.main">
                            -1R
                          </Typography>
                        </>
                      )}
                    </Typography>
                    
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      <b>השוואה לתוצאה בפועל:</b>{' '}
                      {(() => {
                        const wouldReachTarget = detailTrade.tpMilestonesReached && 
                                              detailTrade.tpMilestonesReached.includes(selectedMilestone);
                        
                        if (detailTrade.outcome === 'Take Profit' && wouldReachTarget) {
                          return "אותה תוצאה (Take Profit)";
                        } else if (detailTrade.outcome === 'Stop Loss' && !wouldReachTarget) {
                          return "אותה תוצאה (Stop Loss)";
                        } else if (detailTrade.outcome === 'Stop Loss' && wouldReachTarget) {
                          return (
                            <Typography component="span" fontWeight="bold" color="success.main">
                              שיפור: מ-Stop Loss ל-Take Profit
                            </Typography>
                          );
                        } else {
                          return (
                            <Typography component="span" fontWeight="bold" color="error.main">
                              הרעה: מ-Take Profit ל-Stop Loss
                            </Typography>
                          );
                        }
                      })()}
                    </Typography>
                  </Box>
                </Grid>
              )}
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDetailDialogOpen(false)}>סגור</Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );

  // Renderizado del cargador
  if (loading) {
    return (
      <Box 
        sx={{ 
          display: 'flex', 
          flexDirection: 'column',
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '100vh',
          bgcolor: alpha(theme.palette.primary.main, 0.05),
        }}
      >
        <CircularProgress size={60} thickness={4} />
        <Typography variant="h6" sx={{ mt: 3, fontWeight: 'bold' }}>
          טוען נתונים...
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          מאחזר את היסטוריית העסקאות ומבצע סימולציה
        </Typography>
      </Box>
    );
  }

  // Renderizado principal
  return (
    <Box sx={styles.root}>
      {/* Diálogo de detalles */}
      <TradeDetailDialog />
      
      {/* Alerta */}
      {alertOpen && (
        <Alert 
          severity={alertSeverity}
          onClose={() => setAlertOpen(false)}
          sx={{ mb: 2 }}
        >
          {alertMessage}
        </Alert>
      )}
      
      {/* Contenido principal */}
      <Box component="main" sx={styles.mainContent}>
        {/* Encabezado de página */}
        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h4" fontWeight="bold" color="primary">
              סימולטור אסטרטגיית יציאה
            </Typography>
            <Typography variant="body1" color="text.secondary">
              בדיקת תוצאות אפשריות עם יציאה קבועה בנקודות TP שונות
            </Typography>
          </Box>
          <Button 
            variant="outlined" 
            color="primary" 
            startIcon={<ArrowBackIcon />}
            onClick={handleBack}
          >
           יציאה מהסימולטור 
          </Button>
        </Box>

        {/* User selector */}
        <UserSelector />
        
        {/* Mensaje de sin operaciones */}
        {trades.length === 0 ? (
          <Box 
            sx={{ 
              display: 'flex', 
              flexDirection: 'column',
              justifyContent: 'center', 
              alignItems: 'center', 
              height: '60vh',
              bgcolor: alpha(theme.palette.primary.main, 0.05),
            }}
          >
            <Box sx={{ position: 'relative', mb: 4 }}>
              <ShowChartIcon sx={{ fontSize: 80, color: alpha(theme.palette.text.secondary, 0.2) }} />
              <WarningIcon sx={{ 
                fontSize: 30, 
                color: theme.palette.warning.main,
                position: 'absolute',
                bottom: -5,
                right: -5,
                bgcolor: 'white',
                borderRadius: '50%',
                padding: '3px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
              }} />
            </Box>
            <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 2 }}>
              לא נמצאו עסקאות
            </Typography>
            <Typography variant="body1" color="text.secondary" align="center" sx={{ mb: 3, maxWidth: 500 }}>
              {selectedUsers.length === 0 ? 
                "נא לבחור משתמש אחד או יותר כדי להציג את העסקאות" : 
                "לא נמצאו עסקאות למשתמשים שנבחרו. נסה לבחור משתמשים אחרים או להוסיף עסקאות חדשות."
              }
            </Typography>
            {selectedUsers.length === 0 && (
              <Button
                variant="contained"
                color="primary"
                onClick={handleToggleUserSelector}
                startIcon={<PeopleIcon />}
                sx={{ mb: 2 }}
              >
                בחר משתמשים
              </Button>
            )}
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={handleBack}
            >
             יציאה מהסימולטור סטטיסטי
            </Button>
          </Box>
        ) : (
          <>
            {/* Filtros */}
            <Paper sx={{ p: 2, mb: 3, borderRadius: 2 }}>
              <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
                <FilterListIcon sx={{ mr: 1 }} /> סינון נתונים
              </Typography>
              
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>אסטרטגיית יציאה</InputLabel>
                    <Select
                      value={selectedMilestone}
                      onChange={handleMilestoneChange}
                      label="אסטרטגיית יציאה"
                    >
                      {availableMilestones.map((milestone) => (
                        <MenuItem key={milestone} value={milestone}>
                          {milestone === 'all' ? 'בחר אסטרטגיה' : `יציאה ב-${milestone}`}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>תקופה</InputLabel>
                    <Select
                      value={timeFilter}
                      onChange={(e) => setTimeFilter(e.target.value)}
                      label="תקופה"
                    >
                      <MenuItem value="all">כל הזמנים</MenuItem>
                      <MenuItem value="last30">30 ימים אחרונים</MenuItem>
                      <MenuItem value="last90">90 ימים אחרונים</MenuItem>
                      <MenuItem value="thisYear">שנה נוכחית</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>סימבול</InputLabel>
                    <Select
                      value={symbolFilter}
                      onChange={(e) => setSymbolFilter(e.target.value)}
                      label="סימבול"
                    >
                      {availableSymbols.map((symbol) => (
                        <MenuItem key={symbol} value={symbol}>
                          {symbol === 'all' ? 'כל הסימבולים' : symbol}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>סשן</InputLabel>
                    <Select
                      value={sessionFilter}
                      onChange={(e) => setSessionFilter(e.target.value)}
                      label="סשן"
                    >
                      {availableSessions.map((session) => (
                        <MenuItem key={session} value={session}>
                          {session === 'all' ? 'כל הסשנים' : session}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>יום</InputLabel>
                    <Select
                      value={dayFilter}
                      onChange={(e) => setDayFilter(e.target.value)}
                      label="יום"
                    >
                      {availableDays.map((day) => (
                        <MenuItem key={day} value={day}>
                          {day === 'all' ? 'כל הימים' : day}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
              
      
            </Paper>
            
            {/* Alerta si no hay operaciones con TRIGGER */}
            {filteredTrades.length === 0 && trades.length > 0 && (
              <Alert severity="warning" sx={{ mb: 3 }}>
                לא נמצאו עסקאות עם TRIGGER בתקופה ובסימבול המבוקשים. יש לך {trades.length} עסקאות בסך הכל, אבל רק {trades.filter(t => t.triggerAppeared === 'yes').length} מהן עם TRIGGER.
              </Alert>
            )}
            
            {/* Alerta de punto de salida óptimo */}
            {optimalExitPoint && (
              <Box sx={styles.infoBox}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <EmojiEventsIcon color="warning" sx={{ mr: 2, fontSize: 30 }} />
                  <Box>
                    <Typography variant="h6" fontWeight="bold">
                      נקודת היציאה האופטימלית שלך: {optimalExitPoint}
                    </Typography>
                    <Typography variant="body2">
                      בהתבסס על היסטוריית המסחר שלך, יציאה עקבית בנקודה זו היתה מובילה לתוצאות הטובות ביותר
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 2 }}>
                  <Chip 
                    label={`סה"כ R: ${simulationResults[optimalExitPoint]?.totalR.toFixed(2) || 0}`} 
                    color="success" 
                    variant="outlined" 
                    icon={<MonetizationOnIcon />}
                  />
                  <Chip 
                    label={`אחוז הצלחה: ${simulationResults[optimalExitPoint]?.winRate.toFixed(1) || 0}%`} 
                    color="primary" 
                    variant="outlined" 
                    icon={<CheckCircleIcon />}
                  />
                  <Chip 
                    label={`פקטור רווח: ${simulationResults[optimalExitPoint]?.profitFactor.toFixed(2) || 0}`} 
                    color="info" 
                    variant="outlined" 
                    icon={<EqualizerIcon />}
                  />
                  
                </Box>
              </Box>
            )}
            
            {/* Pestañas de contenido */}
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              variant="scrollable"
              scrollButtons="auto"
              sx={styles.tabs}
            >
              <Tab 
                label="סימולציה" 
                icon={<CompareIcon />} 
                iconPosition="start"
              />
              <Tab 
                label="גרפים מתקדמים" 
                icon={<BarChartIcon />} 
                iconPosition="start"
              />
              <Tab 
                label="ניתוח לפי תקופות" 
                icon={<EventIcon />} 
                iconPosition="start"
              />
              <Tab 
                label="הזדמנויות שהוחמצו" 
                icon={<WarningIcon />} 
                iconPosition="start"
              />
              <Tab 
                label="תקופות ורצפים" 
                icon={<DateRangeIcon />} 
                iconPosition="start"
              />
              <Tab 
                label="ניתוח לפי משתמש" 
                icon={<PeopleIcon />} 
                iconPosition="start"
              />
            </Tabs>
            
            {/* Pestaña 1: Simulación */}
            {activeTab === 0 && (
              <>
                {selectedMilestone !== 'all' && selectedSimulation ? (
                  <>
                    {/* Tarjetas de estadísticas */}
                    <Grid container spacing={3} sx={{ mb: 3 }}>
                      <Grid item xs={12} sm={6} md={3}>
                        <Card sx={styles.statCard}>
                          <CardContent sx={{ textAlign: 'center' }}>
                            <Typography variant="h6" color="text.secondary" gutterBottom>
                              סה"כ עסקאות
                            </Typography>
                            <Typography variant="h3" fontWeight="bold" color="primary.main">
                              {selectedSimulation.totalTrades}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                              {selectedSimulation.winCount} רווח | {selectedSimulation.lossCount} הפסד
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                      
                      <Grid item xs={12} sm={6} md={3}>
                        <Card sx={styles.statCard}>
                          <CardContent sx={{ textAlign: 'center' }}>
                            <Typography variant="h6" color="text.secondary" gutterBottom>
                              אחוז הצלחה
                            </Typography>
                            <Typography variant="h3" fontWeight="bold" color="success.main">
                              {selectedSimulation.winRate.toFixed(1)}%
                            </Typography>
                            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mt: 1 }}>
                              {selectedSimulation.winRate > baselineStats.winRate ? (
                                <>
                                  <ArrowUpwardIcon fontSize="small" color="success" />
                                  <Typography variant="body2" color="success.main">
                                    +{(selectedSimulation.winRate - baselineStats.winRate).toFixed(1)}%
                                  </Typography>
                                </>
                              ) : selectedSimulation.winRate < baselineStats.winRate ? (
                                <>
                                  <ArrowDownwardIcon fontSize="small" color="error" />
                                  <Typography variant="body2" color="error.main">
                                    {(selectedSimulation.winRate - baselineStats.winRate).toFixed(1)}%
                                  </Typography>
                                </>
                              ) : (
                                <Typography variant="body2" color="text.secondary">
                                  ללא שינוי
                                </Typography>
                              )}
                            </Box>
                          </CardContent>
                        </Card>
                      </Grid>
                      
                      <Grid item xs={12} sm={6} md={3}>
                        <Card sx={styles.statCard}>
                          <CardContent sx={{ textAlign: 'center' }}>
                            <Typography variant="h6" color="text.secondary" gutterBottom>
                              סה"כ R
                            </Typography>
                            <Typography 
                              variant="h3" 
                              fontWeight="bold" 
                              color={selectedSimulation.totalR >= 0 ? 'success.main' : 'error.main'}
                            >
                              {selectedSimulation.totalR.toFixed(2)}
                            </Typography>
                            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mt: 1 }}>
                              {selectedSimulation.totalR > baselineStats.totalR ? (
                                <>
                                  <ArrowUpwardIcon fontSize="small" color="success" />
                                  <Typography variant="body2" color="success.main">
                                    +{(selectedSimulation.totalR - baselineStats.totalR).toFixed(2)}
                                  </Typography>
                                </>
                              ) : selectedSimulation.totalR < baselineStats.totalR ? (
                                <>
                                  <ArrowDownwardIcon fontSize="small" color="error" />
                                  <Typography variant="body2" color="error.main">
                                    {(selectedSimulation.totalR - baselineStats.totalR).toFixed(2)}
                                  </Typography>
                                </>
                              ) : (
                                <Typography variant="body2" color="text.secondary">
                                  ללא שינוי
                                </Typography>
                              )}
                            </Box>
                          </CardContent>
                        </Card>
                      </Grid>
                      
                      <Grid item xs={12} sm={6} md={3}>
  <Card sx={styles.statCard}>
    <CardContent sx={{ textAlign: 'center' }}>
      <Typography variant="h6" color="text.secondary" gutterBottom>
        רצף ניצחונות
      </Typography>
      <Typography variant="h3" fontWeight="bold" color="success.main">
        {(selectedMilestone === 'all' ? streakData : simulatedStreakData).winStreaks[0]?.length || 0}
      </Typography>
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mt: 1 }}>
      </Box>
    </CardContent>
  </Card>
</Grid>                    </Grid>
<Grid item xs={12} sm={6} md={3}>
  <Card sx={styles.statCard}>
    <CardContent sx={{ textAlign: 'center' }}>
      <Typography variant="h6" color="text.secondary" gutterBottom>
        רצף הפסדים
      </Typography>
      <Typography variant="h3" fontWeight="bold" color="error.main">
        {(selectedMilestone === 'all' ? streakData : simulatedStreakData).lossStreaks[0]?.length || 0}
      </Typography>
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mt: 1 }}>
      </Box>
    </CardContent>
  </Card>
</Grid>
                    {/* Análisis de cambios */}
                    <Grid container spacing={3}>
                      <Grid item xs={12} md={6}>
                        <Paper sx={styles.card}>
                          <Box sx={styles.header}>
                            <Typography variant="h6" fontWeight="bold">
                              <CompareIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                              השוואת תוצאות: מצב נוכחי מול יציאה ב-{selectedMilestone}
                            </Typography>
                          </Box>
                          <Box sx={{ p: 2 }}>
                            <ResponsiveContainer width="100%" height={350}>
                              <BarChart
                                data={[
                                  { 
                                    name: 'מצב נוכחי', 
                                    totalR: baselineStats.totalR,
                                    winRate: baselineStats.winRate,
                                    profitFactor: baselineStats.profitFactor
                                  },
                                  { 
                                    name: `יציאה ב-${selectedMilestone}`, 
                                    totalR: selectedSimulation.totalR,
                                    winRate: selectedSimulation.winRate,
                                    profitFactor: selectedSimulation.profitFactor
                                  }
                                ]}
                                margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                              >
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="name" />
                                <YAxis />
                                <RechartsTooltip />
                                <Legend />
                                <Bar dataKey="totalR" name="סה״כ R" fill={theme.palette.primary.main}>
                                  {[
                                    { value: baselineStats.totalR },
                                    { value: selectedSimulation.totalR }
                                  ].map((entry, index) => (
                                    <Cell 
                                      key={`cell-${index}`} 
                                      fill={index === 0 ? theme.palette.primary.main : 
                                           (entry.value > baselineStats.totalR ? theme.palette.success.main : theme.palette.error.main)}
                                    />
                                  ))}
                                </Bar>
                                <Bar dataKey="winRate" name="אחוז הצלחה" fill={theme.palette.info.main}>
                                  {[
                                    { value: baselineStats.winRate },
                                    { value: selectedSimulation.winRate }
                                  ].map((entry, index) => (
                                    <Cell 
                                      key={`cell-${index}`} 
                                      fill={index === 0 ? theme.palette.info.main : 
                                           (entry.value > baselineStats.winRate ? theme.palette.success.main : theme.palette.error.main)}
                                    />
                                  ))}
                                </Bar>
                                <Bar dataKey="profitFactor" name="פקטור רווח" fill={theme.palette.warning.main}>
                                  {[
                                    { value: baselineStats.profitFactor },
                                    { value: selectedSimulation.profitFactor }
                                  ].map((entry, index) => (
                                    <Cell 
                                      key={`cell-${index}`} 
                                      fill={index === 0 ? theme.palette.warning.main : 
                                           (entry.value > baselineStats.profitFactor ? theme.palette.success.main : theme.palette.error.main)}
                                    />
                                  ))}
                                </Bar>
                              </BarChart>
                            </ResponsiveContainer>
                          </Box>
                        </Paper>
                      </Grid>
                      
                      <Grid item xs={12} md={6}>
                        <Paper sx={styles.card}>
                          <Box sx={styles.header}>
                            <Typography variant="h6" fontWeight="bold">
                              <TrendingUpIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                              ניתוח השינויים בעסקאות
                            </Typography>
                          </Box>
                          <Box sx={{ p: 2 }}>
                            <Grid container spacing={2}>
                              <Grid item xs={12}>
                                <Box sx={{ 
                                  p: 2, 
                                  borderRadius: 2, 
                                  bgcolor: alpha(theme.palette.success.main, 0.1),
                                  border: `1px solid ${alpha(theme.palette.success.main, 0.3)}`
                                }}>
                                  <Typography variant="subtitle2" fontWeight="bold" color="success.main" gutterBottom>
                                    <ArrowUpwardIcon sx={{ verticalAlign: 'middle', mr: 1 }} fontSize="small" />
                                    {selectedSimulation.improvedTrades.length} עסקאות משתפרות (הפסד ל רווח)
                                  </Typography>
                                  <Typography variant="body2">
                                    עסקאות אלו הסתיימו בסטופ במציאות, אך היו מסתיימות ברווח אם היית יוצא ב-{selectedMilestone} 
                                    כשהעסקה הגיעה ליעד זה.
                                  </Typography>
                                </Box>
                              </Grid>
{/* Upgraded Risk Calculator Component */}
{selectedMilestone !== 'all' && selectedSimulation && (
  <Paper 
    sx={{ 
      p: 0, 
      mb: 3, 
      borderRadius: 2, 
      overflow: 'hidden',
      boxShadow: '0 4px 20px rgba(0,0,0,0.15)'
    }}
  >
    {/* Header Section */}
    <Box 
      sx={{ 
        p: 3, 
        background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.8)} 0%, ${alpha(theme.palette.primary.dark, 0.9)} 100%)`,
        color: 'white',
        position: 'relative',
        overflow: 'hidden'
      }}
    >
      <Box 
        sx={{
          position: 'absolute',
          top: 0,
          right: 0,
          width: '100%',
          height: '100%',
          opacity: 0.1,
          background: 'url("data:image/svg+xml,%3Csvg width=\'100\' height=\'100\' viewBox=\'0 0 100 100\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cpath d=\'M11 18c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm48 25c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm-43-7c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm63 31c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM34 90c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm56-76c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM12 86c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm28-65c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm23-11c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-6 60c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm29 22c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zM32 63c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm57-13c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-9-21c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM60 91c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM35 41c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM12 60c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2z\' fill=\'%23ffffff\' fill-opacity=\'1\' fill-rule=\'evenodd\'/%3E%3C/svg%3E")',
        }}
      />
      
      <Typography variant="h5" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center' }}>
        <AccountBalanceWalletIcon sx={{ mr: 1.5, fontSize: 28 }} />
        מחשבון סיכון אופטימלי
      </Typography>
      
      <Typography variant="body2" sx={{ mt: 1, opacity: 0.9, maxWidth: '80%' }}>
        חשב את אחוז הסיכון האידיאלי לכל עסקה על בסיס הנתונים ההיסטוריים וסבילות הסיכון שלך
      </Typography>
    </Box>
    
    {/* Main Content */}
    <Box sx={{ p: 3 }}>
      <Grid container spacing={3}>
        {/* Input Section */}
        <Grid item xs={12} md={5}>
          <Box sx={{ p: 3, bgcolor: alpha(theme.palette.background.paper, 0.7), borderRadius: 2, height: '100%', boxShadow: '0 0 10px rgba(0,0,0,0.05)' }}>
            <Typography variant="h6" fontWeight="bold" color="primary" gutterBottom>
              הגדרות הסיכון שלך
            </Typography>
            
            <Box sx={{ mt: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="subtitle2">מקסימום דראודאון מקובל</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <TextField
                    type="number"
                    variant="outlined"
                    size="small"
                    value={maxDrawdownInput}
                    onChange={(e) => {
                      const value = parseFloat(e.target.value);
                      if (!isNaN(value) && value > 0) {
                        setMaxDrawdownInput(value);
                      }
                    }}
                    InputProps={{
                      endAdornment: <InputAdornment position="end">%</InputAdornment>,
                    }}
                    sx={{ width: 100, '& input': { textAlign: 'center' } }}
                  />
                </Box>
              </Box>
              
              <Slider
                value={maxDrawdownInput}
                onChange={(e, newValue) => setMaxDrawdownInput(newValue)}
                min={5}
                max={50}
                step={1}
                marks={[
                  { value: 5, label: '5%' },
                  { value: 20, label: '20%' },
                  { value: 35, label: '35%' },
                  { value: 50, label: '50%' }
                ]}
                sx={{ 
                  mt: 2, 
                  color: maxDrawdownInput <= 15 ? theme.palette.success.main : 
                         maxDrawdownInput <= 30 ? theme.palette.warning.main : 
                         theme.palette.error.main,
                  '& .MuiSlider-rail': {
                    opacity: 0.5,
                    backgroundColor: '#bfbfbf',
                  }
                }}
              />
              
              <Box 
                sx={{ 
                  mt: 3, 
                  p: 2, 
                  borderLeft: `4px solid ${theme.palette.info.main}`,
                  bgcolor: alpha(theme.palette.info.main, 0.1),
                  borderRadius: '0 4px 4px 0'
                }}
              >
                <Typography variant="body2" sx={{ fontSize: '0.9rem' }}>
                  <strong>דראודאון</strong> הוא הירידה המקסימלית בתיק ההשקעות שאתה מוכן לספוג. ככל שהערך נמוך יותר, הסיכון שתיקח בכל עסקה יהיה קטן יותר.
                </Typography>
              </Box>
            </Box>
            
            <Box sx={{ mt: 4 }}>
              <Divider sx={{ mb: 2 }} />
              
              <Typography variant="body2" color="text.secondary">
                <strong>נתונים מהסימולציה:</strong>
              </Typography>
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
                <Box>
                  <Typography variant="body2" color="text.secondary">רצף הפסדים מקסימלי</Typography>
                  <Typography variant="h6" color="error.main" fontWeight="bold">
                    {simulatedStreakData.lossStreaks[0]?.length || 0} עסקאות
                  </Typography>
                </Box>
                
                <Box sx={{ textAlign: 'right' }}>
                  <Typography variant="body2" color="text.secondary">סה"כ R מצטבר</Typography>
                  <Typography variant="h6" color="success.main" fontWeight="bold">
                    {selectedSimulation.totalR.toFixed(2)}R
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>
        </Grid>
        
        {/* Results Section */}
        <Grid item xs={12} md={7}>
          <Box sx={{ p: 3, bgcolor: alpha(theme.palette.background.paper, 0.7), borderRadius: 2, height: '100%', boxShadow: '0 0 10px rgba(0,0,0,0.05)' }}>
            <Typography variant="h6" fontWeight="bold" color="primary" gutterBottom>
              התוצאות שלך
            </Typography>
            
            <Box sx={{ 
              mt: 3, 
              p: 3, 
              borderRadius: 2, 
              bgcolor: alpha(theme.palette.primary.main, 0.08),
              border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
              textAlign: 'center',
              position: 'relative',
              overflow: 'hidden'
            }}>
              <Typography variant="subtitle1" gutterBottom>
                אחוז סיכון אופטימלי לכל עסקה:
              </Typography>
              <Typography variant="h3" fontWeight="bold" color="primary.main">
                {calculateOptimalRisk.isValid ? 
                  `${calculateOptimalRisk.riskPerTrade.toFixed(2)}%` : 
                  "N/A"}
              </Typography>
              
              <Typography variant="subtitle1" gutterBottom sx={{ mt: 3 }}>
                סה"כ השפעה פוטנציאלית על התיק:
              </Typography>
              <Typography 
                variant="h3" 
                fontWeight="bold" 
                color={calculateOptimalRisk.totalPortfolioPercentage > 300 ? "error.main" : 
                       calculateOptimalRisk.totalPortfolioPercentage > 200 ? "warning.main" : "success.main"}
              >
                {calculateOptimalRisk.isValid ? 
                  `${calculateOptimalRisk.totalPortfolioPercentage.toFixed(2)}%` : 
                  "N/A"}
              </Typography>
              
              <Box 
                sx={{
                  position: 'absolute',
                  bottom: -25,
                  right: -15,
                  transform: 'rotate(-20deg)',
                  opacity: 0.1,
                  fontSize: 150,
                }}
              >
                %
              </Box>
            </Box>
            
            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle2" gutterBottom>
                <CalculateIcon sx={{ verticalAlign: 'middle', mr: 0.5, fontSize: 18 }} />
                איך החישוב עובד?
              </Typography>
              
              <Box sx={{ mt: 1, ml: 2 }}>
                <Typography variant="body2" gutterBottom>
                  <strong>נוסחה:</strong> אחוז סיכון לעסקה = מקסימום דראודאון ÷ רצף הפסדים ארוך ביותר
                </Typography>
                
                <Typography variant="body2" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                  <strong>עבורך:</strong> 
                  <Chip 
                    label={`${maxDrawdownInput}%`} 
                    color="primary" 
                    size="small" 
                    sx={{ mx: 1, fontWeight: 'bold' }}
                  /> ÷ 
                  <Chip 
                    label={simulatedStreakData.lossStreaks[0]?.length || "?"} 
                    color="error" 
                    size="small" 
                    sx={{ mx: 1, fontWeight: 'bold' }}
                  /> = 
                  <Chip 
                    label={calculateOptimalRisk.isValid ? `${calculateOptimalRisk.riskPerTrade.toFixed(2)}%` : "?"} 
                    color="success" 
                    size="small" 
                    sx={{ mx: 1, fontWeight: 'bold' }}
                  />
                </Typography>
                
                <Typography variant="body2" gutterBottom sx={{ mt: 2 }}>
                  <strong>סה"כ השפעה על התיק:</strong> אחוז סיכון לעסקה × סה"כ R מצטבר
                </Typography>
                
                <Typography variant="body2" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                  <Chip 
                    label={calculateOptimalRisk.isValid ? `${calculateOptimalRisk.riskPerTrade.toFixed(2)}%` : "?"} 
                    color="primary" 
                    size="small" 
                    sx={{ mx: 1, fontWeight: 'bold' }}
                  /> × 
                  <Chip 
                    label={selectedSimulation.totalR?.toFixed(2) || "?"} 
                    color="info" 
                    size="small" 
                    sx={{ mx: 1, fontWeight: 'bold' }}
                  /> = 
                  <Chip 
                    label={calculateOptimalRisk.isValid ? `${calculateOptimalRisk.totalPortfolioPercentage.toFixed(2)}%` : "?"} 
                    color={calculateOptimalRisk.totalPortfolioPercentage > 300 ? "error" : 
                           calculateOptimalRisk.totalPortfolioPercentage > 200 ? "warning" : "success"}
                    size="small" 
                    sx={{ mx: 1, fontWeight: 'bold' }}
                  />
                </Typography>
              </Box>
            </Box>
          </Box>
        </Grid>
      </Grid>
      
      {/* Recommendation Section */}
      <Paper 
      
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
          <InfoIcon sx={{ color: theme.palette.warning.main, mr: 2, fontSize: 24 }} />
          <Box>
          </Box>
        </Box>
      </Paper>
    </Box>
  </Paper>
)}
                              <Grid item xs={12}>
                                <Box sx={{ 
                                  p: 2, 
                                  borderRadius: 2, 
                                  bgcolor: alpha(theme.palette.error.main, 0.1),
                                  border: `1px solid ${alpha(theme.palette.error.main, 0.3)}`
                                }}>
                                  <Typography variant="subtitle2" fontWeight="bold" color="error.main" gutterBottom>
                                    <ArrowDownwardIcon sx={{ verticalAlign: 'middle', mr: 1 }} fontSize="small" />
                                    {selectedSimulation.worseTrades.length} עסקאות נפגעות (רווח ל הפסד)
                                  </Typography>
                                  <Typography variant="body2">
                                    עסקאות אלו הסתיימו ברווח במציאות, אך היו מסתיימות בהפסד כי לא הגיעו ליעד {selectedMilestone}.
                                  </Typography>
                                </Box>
                              </Grid>
                              
                              <Grid item xs={12}>
                                <ResponsiveContainer width="100%" height={250}>
                                  <PieChart>
                                    <Pie
                                      data={[
                                        { 
                                          name: 'עסקאות משתפרות', 
                                          value: selectedSimulation.improvedTrades.length,
                                          fill: theme.palette.success.main
                                        },
                                        { 
                                          name: 'עסקאות נפגעות', 
                                          value: selectedSimulation.worseTrades.length,
                                          fill: theme.palette.error.main
                                        },
                                        { 
                                          name: 'ללא שינוי', 
                                          value: selectedSimulation.unchangedTrades.length,
                                          fill: theme.palette.grey[300]
                                        }
                                      ]}
                                      cx="50%"
                                      cy="50%"
                                      outerRadius={80}
                                      innerRadius={40}
                                      dataKey="value"
                                      label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(1)}%`}
                                    />
                                    <RechartsTooltip />
                                    <Legend />
                                  </PieChart>
                                </ResponsiveContainer>
                              </Grid>
                            </Grid>
                          </Box>
                        </Paper>
                      </Grid>
                    </Grid>
                    
                    {/* Tabla de operaciones simuladas */}
                    <Box sx={{ mt: 3 }}>
                      <Paper sx={styles.tableContainer}>
                        <Box 
                          sx={{ 
                            py: 1.5, 
                            px: 2, 
                            bgcolor: alpha(theme.palette.primary.main, 0.1), 
                            display: 'flex', 
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`
                          }}
                        >
                          <Typography variant="h6" fontWeight="bold" color="primary.main">
                            פירוט העסקאות עם יציאה ב-{selectedMilestone}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            סה"כ: {filteredTrades.length} עסקאות
                          </Typography>
                        </Box>
                        <TableContainer sx={{ maxHeight: 600 }}>
                          <Table stickyHeader>
                            <TableHead>
                              <TableRow>
                                <TableCell sx={styles.tableHeaderCell}>תאריך</TableCell>
                                <TableCell sx={styles.tableHeaderCell}>סימבול</TableCell>
                                <TableCell sx={styles.tableHeaderCell}>משתמש</TableCell>
                                <TableCell sx={styles.tableHeaderCell}>תוצאה בפועל</TableCell>
                                <TableCell sx={styles.tableHeaderCell}>הגיע ל-{selectedMilestone}?</TableCell>
                                <TableCell sx={styles.tableHeaderCell}>תוצאה בסימולציה</TableCell>
                                <TableCell sx={styles.tableHeaderCell}>שינוי</TableCell>
                                <TableCell sx={styles.tableHeaderCell}>פעולות</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {filteredTrades.map((trade) => {
                                // Buscar la simulación de esta operación
                                const simTrade = selectedSimulation.simulatedTrades.find(t => t.id === trade.id);
                                if (!simTrade) return null;
                                
                                // Verificar si la operación alcanzó el milestone seleccionado
                                const reachedMilestone = trade.tpMilestonesReached && 
                                                        Array.isArray(trade.tpMilestonesReached) && 
                                                        trade.tpMilestonesReached.includes(selectedMilestone);
                                
                                // Determinar el tipo de cambio
                                let changeType = 'none';
                                if (trade.outcome === 'Take Profit' && simTrade.simulatedOutcome === 'Stop Loss') {
                                  changeType = 'worse';
                                } else if (trade.outcome === 'Stop Loss' && simTrade.simulatedOutcome === 'Take Profit') {
                                  changeType = 'better';
                                }
                                
                                return (
                                  <TableRow 
                                    key={trade.id} 
                                    hover
                                    sx={{
                                      bgcolor: changeType === 'better' ? alpha(theme.palette.success.main, 0.1) :
                                              changeType === 'worse' ? alpha(theme.palette.error.main, 0.1) :
                                              'transparent'
                                    }}
                                  >
                                    <TableCell>{trade.date}</TableCell>
                                    <TableCell>{trade.symbol}</TableCell>
                                    <TableCell>
                                      <Tooltip title={trade.userEmail}>
                                        <span>{trade.userEmail.split('@')[0]}</span>
                                      </Tooltip>
                                    </TableCell>
                                    <TableCell>
                                      {trade.outcome === 'Take Profit' ? (
                                        <Chip label="Take Profit" color="success" size="small" />
                                      ) : (
                                        <Chip label="Stop Loss" color="error" size="small" />
                                      )}
                                    </TableCell>
                                    <TableCell>
                                      {reachedMilestone ? (
                                        <Chip 
                                          icon={<CheckCircleIcon />} 
                                          label="כן" 
                                          color="success" 
                                          size="small" 
                                          variant="outlined"
                                        />
                                      ) : (
                                        <Chip 
                                          icon={<WarningIcon />} 
                                          label="לא" 
                                          color="error" 
                                          size="small" 
                                          variant="outlined"
                                        />
                                      )}
                                    </TableCell>
                                    <TableCell>
                                      {simTrade.simulatedOutcome === 'Take Profit' ? (
                                        <Tooltip title={`רווח של ${selectedSimulation.targetR}R`}>
                                          <Chip label="Take Profit" color="success" size="small" />
                                        </Tooltip>
                                      ) : (
                                        <Tooltip title="הפסד של 1R">
                                          <Chip label="Stop Loss" color="error" size="small" />
                                        </Tooltip>
                                      )}
                                    </TableCell>
                                    <TableCell>
                                      {changeType === 'better' && (
                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                          <ArrowUpwardIcon color="success" fontSize="small" />
                                          <Typography variant="body2" color="success.main" fontWeight="bold">
                                            שיפור
                                          </Typography>
                                        </Box>
                                      )}
                                      {changeType === 'worse' && (
                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                          <ArrowDownwardIcon color="error" fontSize="small" />
                                          <Typography variant="body2" color="error.main" fontWeight="bold">
                                            הרעה
                                          </Typography>
                                        </Box>
                                      )}
                                      {changeType === 'none' && (
                                        <Typography variant="body2" color="text.secondary">
                                          ללא שינוי
                                        </Typography>
                                      )}
                                    </TableCell>
                                    <TableCell>
                                      <Button 
                                        size="small" 
                                        variant="outlined"
                                        startIcon={<InfoIcon />}
                                        onClick={() => handleDetailClick(trade)}
                                        >
                                          פרטים
                                        </Button>
                                      </TableCell>
                                    </TableRow>
                                  );
                                })}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </Paper>
                      </Box>
                    </>
                  ) : (
                    <>
                      {/* Gráfico de comparación de todos los milestones */}
                      <Paper sx={styles.card}>
                        <Box sx={styles.header}>
                          <Typography variant="h6" fontWeight="bold">
                            <CompareIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                            השוואת אסטרטגיות יציאה
                          </Typography>
                        </Box>
                        <Box sx={{ p: 2 }}>
                          <ResponsiveContainer width="100%" height={400}>
                            <ComposedChart
                              data={comparisonData}
                              margin={{ top: 20, right: 30, left: 30, bottom: 20 }}
                            >
                              <CartesianGrid strokeDasharray="3 3" />
                              <XAxis dataKey="name" />
                              <YAxis yAxisId="left" orientation="left" label={{ value: 'ערכי R', angle: -90, position: 'insideLeft' }} />
                              <YAxis yAxisId="right" orientation="right" domain={[0, 100]} label={{ value: 'אחוז הצלחה (%)', angle: 90, position: 'insideRight' }} />
                              <RechartsTooltip content={<CustomTooltip />} />
                              <Legend />
                              <Bar 
                                yAxisId="left" 
                                dataKey="totalR" 
                                name="סה״כ R" 
                                fill={theme.palette.primary.main}
                                onClick={(data) => handleMilestoneChange({ target: { value: data.name } })}
                              >
                                {comparisonData.map((entry, index) => (
                                  <Cell 
                                    key={`cell-${index}`} 
                                    fill={entry.totalR > baselineStats.totalR ? theme.palette.success.main : theme.palette.error.main}
                                    style={{ cursor: 'pointer' }}
                                  />
                                ))}
                              </Bar>
                              <Line 
                                yAxisId="right" 
                                type="monotone" 
                                dataKey="winRate" 
                                name="אחוז הצלחה" 
                                stroke={theme.palette.info.main}
                                strokeWidth={2}
                                dot={{ r: 5 }}
                              />
                            </ComposedChart>
                          </ResponsiveContainer>
                        </Box>
                        <Box sx={{ p: 2, textAlign: 'center' }}>
                          <Typography variant="body1" fontWeight="bold" sx={{ mb: 1 }}>
                            בחר אסטרטגיית יציאה מהתפריט או לחץ על העמודה המתאימה בגרף כדי לראות סימולציה מפורטת
                          </Typography>
                          {optimalExitPoint && (
                            <Typography variant="body2" color="text.secondary">
                              אסטרטגיית היציאה האופטימלית נראית כמו {optimalExitPoint}
                            </Typography>
                          )}
                        </Box>
                      </Paper>
                      
                      {/* Tabla de comparación */}
                      <Box sx={{ mt: 3 }}>
                        <Paper sx={styles.tableContainer}>
                          <Box 
                            sx={{ 
                              py: 1.5, 
                              px: 2, 
                              bgcolor: alpha(theme.palette.primary.main, 0.1), 
                              display: 'flex', 
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`
                            }}
                          >
                            <Typography variant="h6" fontWeight="bold" color="primary.main">
                              השוואת אסטרטגיות יציאה
                            </Typography>
                          </Box>
                          <TableContainer>
                            <Table>
                              <TableHead>
                                <TableRow>
                                  <TableCell sx={styles.tableHeaderCell}>נקודת יציאה</TableCell>
                                  <TableCell sx={styles.tableHeaderCell}>אחוז הצלחה</TableCell>
                                  <TableCell sx={styles.tableHeaderCell}>סה"כ R</TableCell>
                                  <TableCell sx={styles.tableHeaderCell}>ממוצע R</TableCell>
                                  <TableCell sx={styles.tableHeaderCell}>פקטור רווח</TableCell>
                                  <TableCell sx={styles.tableHeaderCell}>משתפרות</TableCell>
                                  <TableCell sx={styles.tableHeaderCell}>נפגעות</TableCell>
                                  <TableCell sx={styles.tableHeaderCell}>פעולות</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {/* Fila base - resultados actuales */}
                                <TableRow 
                                  hover
                                  sx={{
                                    bgcolor: alpha(theme.palette.primary.main, 0.08),
                                    '&:hover': {
                                      bgcolor: alpha(theme.palette.primary.main, 0.12),
                                    }
                                  }}
                                >
                                  <TableCell sx={{ fontWeight: 'bold' }}>מצב נוכחי</TableCell>
                                  <TableCell>{baselineStats.winRate.toFixed(1)}%</TableCell>
                                  <TableCell>{baselineStats.totalR.toFixed(2)}</TableCell>
                                  <TableCell>{baselineStats.avgR.toFixed(2)}</TableCell>
                                  <TableCell>{baselineStats.profitFactor.toFixed(2)}</TableCell>
                                  <TableCell>-</TableCell>
                                  <TableCell>-</TableCell>
                                  <TableCell>-</TableCell>
                                </TableRow>
                                
                                {/* Ordenar resultados por puntuación */}
                                {Object.values(simulationResults)
                                  .sort((a, b) => b.score - a.score)
                                  .map((sim, index) => (
                                  <TableRow 
                                    key={sim.milestone}
                                    hover
                                    sx={{
                                      cursor: 'pointer',
                                      bgcolor: sim.milestone === optimalExitPoint ? alpha(theme.palette.success.main, 0.08) : 'transparent',
                                      '&:hover': {
                                        bgcolor: sim.milestone === optimalExitPoint ? 
                                          alpha(theme.palette.success.main, 0.12) : 
                                          alpha(theme.palette.primary.main, 0.05),
                                      }
                                    }}
                                    onClick={() => handleMilestoneChange({ target: { value: sim.milestone } })}
                                  >
                                    <TableCell sx={{ fontWeight: 'bold' }}>
                                      {sim.milestone === optimalExitPoint ? (
                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                          <EmojiEventsIcon sx={{ color: theme.palette.warning.main, mr: 1 }} fontSize="small" />
                                          {sim.milestone}
                                        </Box>
                                      ) : (
                                        sim.milestone
                                      )}
                                    </TableCell>
                                    <TableCell 
                                      sx={{ 
                                        color: sim.winRate > baselineStats.winRate ? theme.palette.success.main : 
                                               sim.winRate < baselineStats.winRate ? theme.palette.error.main :
                                               'inherit'
                                      }}
                                    >
                                      {sim.winRate.toFixed(1)}%
                                      {sim.winRate !== baselineStats.winRate && (
                                        <Typography component="span" variant="body2" sx={{ ml: 1 }}>
                                          ({sim.winRate > baselineStats.winRate ? '+' : ''}{(sim.winRate - baselineStats.winRate).toFixed(1)}%)
                                        </Typography>
                                      )}
                                    </TableCell>
                                    <TableCell 
                                      sx={{ 
                                        color: sim.totalR > baselineStats.totalR ? theme.palette.success.main : 
                                               sim.totalR < baselineStats.totalR ? theme.palette.error.main :
                                               'inherit',
                                        fontWeight: 'bold'
                                      }}
                                    >
                                      {sim.totalR.toFixed(2)}
                                      {sim.totalR !== baselineStats.totalR && (
                                        <Typography component="span" variant="body2" sx={{ ml: 1 }}>
                                          ({sim.totalR > baselineStats.totalR ? '+' : ''}{(sim.totalR - baselineStats.totalR).toFixed(2)})
                                        </Typography>
                                      )}
                                    </TableCell>
                                    <TableCell 
                                      sx={{ 
                                        color: sim.avgR > baselineStats.avgR ? theme.palette.success.main : 
                                               sim.avgR < baselineStats.avgR ? theme.palette.error.main :
                                               'inherit'
                                      }}
                                    >
                                      {sim.avgR.toFixed(2)}
                                    </TableCell>
                                    <TableCell 
                                      sx={{ 
                                        color: sim.profitFactor > baselineStats.profitFactor ? theme.palette.success.main : 
                                               sim.profitFactor < baselineStats.profitFactor ? theme.palette.error.main :
                                               'inherit'
                                      }}
                                    >
                                      {sim.profitFactor.toFixed(2)}
                                    </TableCell>
                                    <TableCell>
                                      <Chip 
                                        label={sim.improvedTrades.length} 
                                        size="small" 
                                        color="success"
                                      />
                                    </TableCell>
                                    <TableCell>
                                      <Chip 
                                        label={sim.worseTrades.length} 
                                        size="small" 
                                        color="error"
                                      />
                                    </TableCell>
                                    <TableCell>
                                      <Button
                                        size="small"
                                        variant="outlined"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleMilestoneChange({ target: { value: sim.milestone } });
                                        }}
                                      >
                                        הצג
                                      </Button>
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </Paper>
                      </Box>
                    </>
                  )}
                </>
              )}
              
              {/* Pestaña 2: Gráficos avanzados */}
              {activeTab === 1 && (
                <>
                  {selectedMilestone === 'all' ? (
                    <Alert severity="info" sx={{ mb: 3 }}>
                      נא לבחור אסטרטגיית יציאה כדי לראות את הגרפים המתקדמים
                    </Alert>
                  ) : (
                    <Grid container spacing={3}>
                      {/* Gráfico radar de métricas clave */}
                      <Grid item xs={12} md={6}>
                        <Paper sx={styles.card}>
                          <Box sx={styles.header}>
                            <Typography variant="h6" fontWeight="bold">
                              <BubbleChartIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                              מדדי ביצוע מרכזיים
                            </Typography>
                          </Box>
                          <Box sx={{ p: 2, height: 400 }}>
                            <ResponsiveContainer width="100%" height="100%">
                              <RadarChart 
                                cx="50%" 
                                cy="50%" 
                                outerRadius="80%" 
                                data={[
                                  {
                                    subject: 'Win Rate',
                                    A: baselineStats.winRate,
                                    B: selectedSimulation.winRate,
                                    fullMark: 100,
                                  },
                                  {
                                    subject: 'Profit Factor',
                                    A: baselineStats.profitFactor * 10, // Scale up for visualization
                                    B: selectedSimulation.profitFactor * 10,
                                    fullMark: 100,
                                  },
                                  {
                                    subject: 'R Value',
                                    // Scale to percentage of the max between the two
                                    A: baselineStats.totalR > 0 ? 
                                       (baselineStats.totalR / Math.max(baselineStats.totalR, selectedSimulation.totalR)) * 100 : 0,
                                    B: selectedSimulation.totalR > 0 ? 
                                       (selectedSimulation.totalR / Math.max(baselineStats.totalR, selectedSimulation.totalR)) * 100 : 0,
                                    fullMark: 100,
                                  },
                                  {
                                    subject: 'Expectancy',
                                    // Scale to percentage
                                    A: baselineStats.avgR > 0 ? (baselineStats.avgR * 25) : 0,
                                    B: selectedSimulation.avgR > 0 ? (selectedSimulation.avgR * 25) : 0,
                                    fullMark: 100,
                                  },
                                  {
                                    subject: 'Trade Count',
                                    A: 80, // Always the same in our simulation
                                    B: 80,
                                    fullMark: 100,
                                  }
                                ]}
                              >
                                <PolarGrid />
                                <PolarAngleAxis dataKey="subject" />
                                <PolarRadiusAxis angle={30} domain={[0, 100]} />
                                <Radar 
                                  name="מצב נוכחי" 
                                  dataKey="A" 
                                  stroke={theme.palette.primary.main} 
                                  fill={theme.palette.primary.main} 
                                  fillOpacity={0.5} 
                                />
                                <Radar 
                                  name={`יציאה ב-${selectedMilestone}`} 
                                  dataKey="B" 
                                  stroke={theme.palette.secondary.main} 
                                  fill={theme.palette.secondary.main} 
                                  fillOpacity={0.5} 
                                />
                                <Legend />
                              </RadarChart>
                            </ResponsiveContainer>
                          </Box>
                        </Paper>
                      </Grid>
  
                      {/* Análisis por símbolo */}
                      <Grid item xs={12} md={6}>
                        <Paper sx={styles.card}>
                          <Box sx={styles.header}>
                            <Typography variant="h6" fontWeight="bold">
                              <DonutLargeIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                              ביצועים לפי סימבול
                            </Typography>
                          </Box>
                          <Box sx={{ p: 2, height: 400 }}>
                            <ResponsiveContainer width="100%" height="100%">
                              <BarChart
                                data={selectedSimulation.symbolAnalysis}
                                margin={{ top: 20, right: 30, left: 20, bottom: 50 }}
                                layout="vertical"
                              >
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis type="number" />
                                <YAxis dataKey="symbol" type="category" width={80} />
                                <RechartsTooltip />
                                <Legend />
                                <Bar dataKey="winRate" name="אחוז הצלחה" fill={theme.palette.info.main} />
                                <Bar dataKey="totalR" name="סה״כ R" fill={theme.palette.success.main} />
                              </BarChart>
                            </ResponsiveContainer>
                          </Box>
                        </Paper>
                      </Grid>
  
                      {/* Análisis por sesión */}
                      <Grid item xs={12} md={6}>
                        <Paper sx={styles.card}>
                          <Box sx={styles.header}>
                            <Typography variant="h6" fontWeight="bold">
                              <PieChartIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                              ביצועים לפי סשן
                            </Typography>
                          </Box>
                          <Box sx={{ p: 2, height: 400 }}>
                            <ResponsiveContainer width="100%" height="100%">
                              <PieChart>
                                <Pie
                                  data={selectedSimulation.sessionAnalysis}
                                  cx="50%"
                                  cy="50%"
                                  labelLine={false}
                                  label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(1)}%`}
                                  outerRadius={120}
                                  fill="#8884d8"
                                  dataKey="totalR"
                                >
                                  {selectedSimulation.sessionAnalysis.map((entry, index) => (
                                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                  ))}
                                </Pie>
                                <RechartsTooltip />
                                <Legend />
                              </PieChart>
                            </ResponsiveContainer>
                          </Box>
                        </Paper>
                      </Grid>
  
                      {/* Análisis por día de la semana */}
                      <Grid item xs={12} md={6}>
                        <Paper sx={styles.card}>
                          <Box sx={styles.header}>
                            <Typography variant="h6" fontWeight="bold">
                              <BarChartIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                              ביצועים לפי יום בשבוע
                            </Typography>
                          </Box>
                          <Box sx={{ p: 2, height: 400 }}>
                            <ResponsiveContainer width="100%" height="100%">
                              <BarChart
                                data={selectedSimulation.dayAnalysis}
                                margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                              >
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="day" />
                                <YAxis />
                                <RechartsTooltip />
                                <Legend />
                                <Bar dataKey="winRate" name="אחוז הצלחה" fill={theme.palette.info.main} />
                                <Bar dataKey="totalR" name="סה״כ R" fill={theme.palette.success.main} />
                              </BarChart>
                            </ResponsiveContainer>
                          </Box>
                        </Paper>
                      </Grid>
  
                      {/* Análisis por usuario */}
                      <Grid item xs={12}>
                        <Paper sx={styles.card}>
                          <Box sx={styles.header}>
                            <Typography variant="h6" fontWeight="bold">
                              <PeopleIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                              ביצועים לפי משתמש
                            </Typography>
                          </Box>
                          <Box sx={{ p: 2, height: 400 }}>
                            <ResponsiveContainer width="100%" height="100%">
                              <BarChart
                                data={selectedSimulation.userAnalysis}
                                margin={{ top: 20, right: 30, left: 20, bottom: 50 }}
                              >
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="userEmail" angle={-45} textAnchor="end" height={80} />
                                <YAxis />
                                <RechartsTooltip />
                                <Legend />
                                <Bar dataKey="winRate" name="אחוז הצלחה" fill={theme.palette.info.main} />
                                <Bar dataKey="totalR" name="סה״כ R" fill={theme.palette.success.main} />
                              </BarChart>
                            </ResponsiveContainer>
                          </Box>
                        </Paper>
                      </Grid>
  
                      {/* Análisis de cambios detallado - Treemap */}
                      <Grid item xs={12}>
                        <Paper sx={styles.card}>
                          <Box sx={styles.header}>
                            <Typography variant="h6" fontWeight="bold">
                              <TrendingUpIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                              ניתוח מפורט של שינויים בעסקאות
                            </Typography>
                          </Box>
                          <Box sx={{ p: 2, height: 500 }}>
                            <ResponsiveContainer width="100%" height="100%">
                              <Treemap
                                data={[
                                  {
                                    name: 'עסקאות משתפרות',
                                    children: selectedSimulation.improvedTrades.map(trade => ({
                                      name: `${trade.symbol} | ${trade.date} | ${trade.userEmail.split('@')[0]}`,
                                      size: selectedSimulation.targetR,
                                      color: theme.palette.success.main
                                    }))
                                  },
                                  {
                                    name: 'עסקאות נפגעות',
                                    children: selectedSimulation.worseTrades.map(trade => ({
                                      name: `${trade.symbol} | ${trade.date} | ${trade.userEmail.split('@')[0]}`,
                                      size: 1, // Loss = 1R loss
                                      color: theme.palette.error.main
                                    }))
                                  },
                                  {
                                    name: 'ללא שינוי (רווח)',
                                    children: selectedSimulation.unchangedTrades
                                      .filter(trade => trade.outcome === 'Take Profit')
                                      .map(trade => {
                                        let r = 1;
                                        if (trade.tpMilestoneExit) {
                                          const parts = trade.tpMilestoneExit.split(':');
                                          if (parts.length === 2) {
                                            r = parseFloat(parts[1]);
                                          }
                                        }
                                        return {
                                          name: `${trade.symbol} | ${trade.date} | ${trade.userEmail.split('@')[0]}`,
                                          size: r,
                                          color: theme.palette.primary.light
                                        };
                                      })
                                  },
                                  {
                                    name: 'ללא שינוי (הפסד)',
                                    children: selectedSimulation.unchangedTrades
                                      .filter(trade => trade.outcome === 'Stop Loss')
                                      .map(trade => ({
                                        name: `${trade.symbol} | ${trade.date} | ${trade.userEmail.split('@')[0]}`,
                                        size: 1, // Loss = 1R loss
                                        color: theme.palette.grey[400]
                                      }))
                                  }
                                ]}
                                dataKey="size"
                                ratio={4 / 3}
                                stroke="#fff"
                                fill="#8884d8"
                                content={<CustomizedContent colors={data => data.color} />}
                              >
                                <RechartsTooltip content={<CustomTooltip />} />
                              </Treemap>
                            </ResponsiveContainer>
                          </Box>
                        </Paper>
                      </Grid>
                    </Grid>
                  )}
                </>
              )}
  
              {/* Pestaña 3: Análisis por períodos */}
              {activeTab === 2 && (
                <>
                  {selectedMilestone === 'all' ? (
                    <Alert severity="info" sx={{ mb: 3 }}>
                      נא לבחור אסטרטגיית יציאה כדי לראות את ניתוח התקופות
                    </Alert>
                  ) : (
                    <Grid container spacing={3}>
                      {/* Rendimiento mensual */}
                      <Grid item xs={12}>
                        <Paper sx={styles.card}>
                          <Box sx={styles.header}>
                            <Typography variant="h6" fontWeight="bold">
                              <TimelineIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                              ביצועים חודשיים
                            </Typography>
                          </Box>
                          <Box sx={{ p: 2, height: 400 }}>
                            <ResponsiveContainer width="100%" height="100%">
                              <ComposedChart
                                data={selectedSimulation.monthlyPerformance}
                                margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                              >
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="month" />
                                <YAxis yAxisId="left" orientation="left" label={{ value: 'ערכי R', angle: -90, position: 'insideLeft' }} />
                                <YAxis yAxisId="right" orientation="right" domain={[0, 100]} label={{ value: 'אחוז הצלחה (%)', angle: 90, position: 'insideRight' }} />
                                <RechartsTooltip />
                                <Legend />
                                <Bar 
                                  yAxisId="left" 
                                  dataKey="totalR" 
                                  name="סה״כ R" 
                                  fill={theme.palette.primary.main}
                                >
                                  {selectedSimulation.monthlyPerformance.map((entry, index) => (
                                    <Cell 
                                      key={`cell-${index}`} 
                                      fill={entry.totalR >= 0 ? theme.palette.success.main : theme.palette.error.main}
                                    />
                                  ))}
                                </Bar>
                                <Line 
                                  yAxisId="right" 
                                  type="monotone" 
                                  dataKey="winRate" 
                                  name="אחוז הצלחה" 
                                  stroke={theme.palette.info.main}
                                  strokeWidth={2}
                                  dot={{ r: 5 }}
                                />
                              </ComposedChart>
                            </ResponsiveContainer>
                          </Box>
                        </Paper>
                      </Grid>
  
                      {/* Tabla de rendimiento mensual */}
                      <Grid item xs={12}>
                        <Paper sx={styles.tableContainer}>
                          <Box 
                            sx={{ 
                              py: 1.5, 
                              px: 2, 
                              bgcolor: alpha(theme.palette.primary.main, 0.1), 
                              display: 'flex', 
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`
                            }}
                          >
                            <Typography variant="h6" fontWeight="bold" color="primary.main">
                              ביצועים חודשיים מפורטים
                            </Typography>
                          </Box>
                          <TableContainer>
                            <Table>
                              <TableHead>
                                <TableRow>
                                  <TableCell sx={styles.tableHeaderCell}>חודש</TableCell>
                                  <TableCell sx={styles.tableHeaderCell}>מס' עסקאות</TableCell>
                                  <TableCell sx={styles.tableHeaderCell}>הצלחות</TableCell>
                                  <TableCell sx={styles.tableHeaderCell}>כשלונות</TableCell>
                                  <TableCell sx={styles.tableHeaderCell}>אחוז הצלחה</TableCell>
                                  <TableCell sx={styles.tableHeaderCell}>סה"כ R</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {selectedSimulation.monthlyPerformance.map((month) => (
                                  <TableRow 
                                    key={month.month}
                                    hover
                                    sx={{
                                      bgcolor: month.totalR > 0 ? alpha(theme.palette.success.main, 0.05) : alpha(theme.palette.error.main, 0.05)
                                    }}
                                  >
                                    <TableCell sx={{ fontWeight: 'bold' }}>{month.month}</TableCell>
                                    <TableCell>{month.totalTrades}</TableCell>
                                    <TableCell>{month.winCount}</TableCell>
                                    <TableCell>{month.lossCount}</TableCell>
                                    <TableCell>{month.winRate.toFixed(1)}%</TableCell>
                                    <TableCell 
                                      sx={{ 
                                        fontWeight: 'bold',
                                        color: month.totalR >= 0 ? theme.palette.success.main : theme.palette.error.main
                                      }}
                                    >
                                      {month.totalR.toFixed(2)}R
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </Paper>
                      </Grid>
  
                      {/* Gráfico de rendimiento acumulado */}
                      <Grid item xs={12}>
                        <Paper sx={styles.card}>
                          <Box sx={styles.header}>
                            <Typography variant="h6" fontWeight="bold">
                              <ShowChartIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                              התקדמות מצטברת
                            </Typography>
                          </Box>
                          <Box sx={{ p: 2, height: 400 }}>
                            <ResponsiveContainer width="100%" height="100%">
                              <LineChart
                                margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                              >
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis 
                                  dataKey="date"
                                  type="category"
                                  allowDuplicatedCategory={false}
                                />
                                <YAxis 
                                  label={{ value: 'R מצטבר', angle: -90, position: 'insideLeft' }}
                                />
                                <RechartsTooltip />
                                <Legend />
                                <Line 
                                  data={(() => {
                                    // Crear datos para el gráfico de rendimiento acumulado real
                                    let cumulative = 0;
                                    return filteredTrades
                                      .sort((a, b) => new Date(a.date) - new Date(b.date))
                                      .map((trade, index) => {
                                        // Calcular el valor R real
                                        let r = trade.outcome === 'Take Profit' ?
                                          (trade.tpMilestoneExit ?
                                            (trade.tpMilestoneExit.split(':').length === 2 ?
                                              parseFloat(trade.tpMilestoneExit.split(':')[1]) : 1) :
                                            1) :
                                          -1;
                                        
                                        cumulative += r;
                                        return {
                                          date: trade.date,
                                          tradeIndex: index + 1,
                                          r,
                                          cumulative
                                        };
                                      });
                                  })()}
                                  type="monotone"
                                  dataKey="cumulative"
                                  name="מצב נוכחי"
                                  stroke={theme.palette.primary.main}
                                  dot={false}
                                  activeDot={{ r: 8 }}
                                />
                                <Line 
                                  data={(() => {
                                    // Crear datos para el gráfico de rendimiento acumulado simulado
                                    let cumulative = 0;
                                    return selectedSimulation.simulatedTrades
                                      .sort((a, b) => new Date(a.date) - new Date(b.date))
                                      .map((trade, index) => {
                                        let r = trade.simulatedR;
                                        cumulative += r;
                                        return {
                                          date: trade.date,
                                          tradeIndex: index + 1,
                                          r,
                                          cumulative
                                        };
                                      });
                                  })()}
                                  type="monotone"
                                  dataKey="cumulative"
                                  name={`יציאה ב-${selectedMilestone}`}
                                  stroke={theme.palette.secondary.main}
                                  dot={false}
                                  activeDot={{ r: 8 }}
                                />
                              </LineChart>
                            </ResponsiveContainer>
                          </Box>
                        </Paper>
                      </Grid>
                    </Grid>
                  )}
                </>
              )}
  
              {/* Pestaña 4: Oportunidades perdidas */}
              {activeTab === 3 && (
                <>
                  {selectedMilestone === 'all' ? (
                    <Alert severity="info" sx={{ mb: 3 }}>
                      נא לבחור אסטרטגיית יציאה כדי לראות את ההזדמנויות שהוחמצו
                    </Alert>
                  ) : (
                    <>
                      {/* Resumen de oportunidades perdidas */}
                      <Box sx={{ mb: 3 }}>
                        <Paper sx={{ p: 3, borderRadius: 2 }}>
                          <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                            <ErrorIcon sx={{ color: theme.palette.warning.main, mr: 2, fontSize: 40 }} />
                            <Box>
                              <Typography variant="h5" fontWeight="bold" gutterBottom>
                                ניתוח הזדמנויות שהוחמצו: {selectedMilestone}
                              </Typography>
                              <Typography variant="body1">
                                {selectedSimulation.missedOpportunities.length === 0 ? (
                                  "מצוין! לא נמצאו הזדמנויות שהוחמצו. כל העסקאות שהגיעו ליעד זה או הסתיימו ברווח או לא הגיעו ליעד בכלל."
                                ) : (
                                  `נמצאו ${selectedSimulation.missedOpportunities.length} עסקאות שהגיעו ליעד ${selectedMilestone} אך הסתיימו בהפסד.`
                                )}
                              </Typography>
                            </Box>
                          </Box>
  
                          {selectedSimulation.missedOpportunities.length > 0 && (
                            <>
                              <Divider sx={{ my: 2 }} />
                              
                              <Typography variant="subtitle1" fontWeight="bold" color="primary.main" gutterBottom>
                                השפעה על הביצועים:
                              </Typography>
                              
                              <Grid container spacing={2} sx={{ mb: 2 }}>
                                <Grid item xs={12} md={4}>
                                  <Box sx={{ 
                                    p: 2, 
                                    borderRadius: 2, 
                                    bgcolor: alpha(theme.palette.warning.main, 0.1),
                                    border: `1px solid ${alpha(theme.palette.warning.main, 0.3)}`
                                  }}>
                                    <Typography variant="h6" align="center" fontWeight="bold">
                                      {(selectedSimulation.targetR * selectedSimulation.missedOpportunities.length).toFixed(1)}R
                                    </Typography>
                                    <Typography variant="body2" align="center">
                                      הפסד פוטנציאלי כולל
                                    </Typography>
                                  </Box>
                                </Grid>
                                
                                <Grid item xs={12} md={4}>
                                  <Box sx={{ 
                                    p: 2, 
                                    borderRadius: 2, 
                                    bgcolor: alpha(theme.palette.info.main, 0.1),
                                    border: `1px solid ${alpha(theme.palette.info.main, 0.3)}`
                                  }}>
                                    <Typography variant="h6" align="center" fontWeight="bold">
                                      {((selectedSimulation.missedOpportunities.length / selectedSimulation.totalTrades) * 100).toFixed(1)}%
                                    </Typography>
                                    <Typography variant="body2" align="center">
                                      אחוז מסך העסקאות
                                    </Typography>
                                  </Box>
                                </Grid>
                                
                                <Grid item xs={12} md={4}>
                                  <Box sx={{ 
                                    p: 2, 
                                    borderRadius: 2, 
                                    bgcolor: alpha(theme.palette.success.main, 0.1),
                                    border: `1px solid ${alpha(theme.palette.success.main, 0.3)}`
                                  }}>
                                    <Typography variant="h6" align="center" fontWeight="bold">
                                      {selectedSimulation.targetR.toFixed(1)}R
                                    </Typography>
                                    <Typography variant="body2" align="center">
                                      רווח פוטנציאלי לעסקה
                                    </Typography>
                                  </Box>
                                </Grid>
                              </Grid>
                              
                              <Typography variant="subtitle1" fontWeight="bold" color="primary.main" gutterBottom>
                                התפלגות סיבות לסטופ:
                              </Typography>
                              
                              {/* Gráfico de razones de stop loss */}
                              <Box sx={{ height: 300, mb: 3 }}>
                                <ResponsiveContainer width="100%" height="100%">
                                  <PieChart>
                                    <Pie
                                      data={(() => {
                                        // Agrupar por razón de stop loss
                                        const reasons = {};
                                        selectedSimulation.missedOpportunities.forEach(trade => {
                                          const reason = trade.stopReason || 'לא צוין';
                                          if (!reasons[reason]) {
                                            reasons[reason] = 0;
                                          }
                                          reasons[reason]++;
                                        });
                                        
                                        return Object.entries(reasons).map(([reason, count], index) => ({
                                          name: reason,
                                          value: count,
                                          fill: COLORS[index % COLORS.length]
                                        }));
                                      })()}
                                      cx="50%"
                                      cy="50%"
                                      outerRadius={100}
                                      innerRadius={40}
                                      labelLine={true}
                                      label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(1)}%`}
                                      dataKey="value"
                                    />
                                    <RechartsTooltip />
                                    <Legend />
                                  </PieChart>
                                </ResponsiveContainer>
                              </Box>
                              
                              <Typography variant="subtitle1" fontWeight="bold" color="primary.main" gutterBottom>
                                המלצות לשיפור:
                              </Typography>
                              
                              <Box sx={{ mb: 2 }}>
                                <List>
                                  <ListItem>
                                    <ListItemIcon>
                                      <Avatar sx={{ bgcolor: theme.palette.success.main }}>1</Avatar>
                                    </ListItemIcon>
                                    <ListItemText
                                      primary="יציאה עקבית ב-TP"
                                      secondary={`שקול לצאת באופן עקבי ביעד ${selectedMilestone} כאשר המחיר מגיע אליו, אפילו אם יש סיכוי להמשך עליה`}
                                    />
                                  </ListItem>
                                  <ListItem>
                                    <ListItemIcon>
                                      <Avatar sx={{ bgcolor: theme.palette.success.main }}>2</Avatar>
                                    </ListItemIcon>
                                    <ListItemText
                                      primary="יציאה חלקית"
                                      secondary={`שקול יציאה חלקית בנקודת ${selectedMilestone} ולאפשר לחלק מהפוזיציה להמשיך לנוע`}
                                    />
                                  </ListItem>
                                  <ListItem>
                                    <ListItemIcon>
                                      <Avatar sx={{ bgcolor: theme.palette.success.main }}>3</Avatar>
                                    </ListItemIcon>
                                    <ListItemText
                                      primary="הזז את הסטופ"
                                      secondary={`שקול להזיז את הסטופ לנקודת הכניסה או לנקודת ה-TP הקודמת כאשר המחיר מגיע ליעד ${selectedMilestone}`}
                                    />
                                  </ListItem>
                                </List>
                              </Box>
                            </>
                          )}
                        </Paper>
                      </Box>
  
                      {/* Tabla de oportunidades perdidas */}
                      {selectedSimulation.missedOpportunities.length > 0 && (
                        <Box>
                          <Paper sx={styles.tableContainer}>
                            <Box 
                              sx={{ 
                                py: 1.5, 
                                px: 2, 
                                bgcolor: alpha(theme.palette.primary.main, 0.1), 
                                display: 'flex', 
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`
                              }}
                            >
                              <Typography variant="h6" fontWeight="bold" color="primary.main">
                                פירוט ההזדמנויות שהוחמצו
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                סה"כ: {selectedSimulation.missedOpportunities.length} עסקאות
                              </Typography>
                            </Box>
                            <TableContainer>
                              <Table>
                                <TableHead>
                                  <TableRow>
                                    <TableCell sx={styles.tableHeaderCell}>תאריך</TableCell>
                                    <TableCell sx={styles.tableHeaderCell}>סימבול</TableCell>
                                    <TableCell sx={styles.tableHeaderCell}>משתמש</TableCell>
                                    <TableCell sx={styles.tableHeaderCell}>סשן</TableCell>
                                    <TableCell sx={styles.tableHeaderCell}>יעדים שהושגו</TableCell>
                                    <TableCell sx={styles.tableHeaderCell}>סיבת סטופ</TableCell>
                                    <TableCell sx={styles.tableHeaderCell}>משך</TableCell>
                                    <TableCell sx={styles.tableHeaderCell}>פעולות</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {selectedSimulation.missedOpportunities.map((trade) => (
                                    <TableRow 
                                      key={trade.id} 
                                      hover
                                      sx={{
                                        bgcolor: alpha(theme.palette.warning.main, 0.05)
                                      }}
                                    >
                                      <TableCell>{trade.date}</TableCell>
                                      <TableCell>{trade.symbol}</TableCell>
                                      <TableCell>
                                        <Tooltip title={trade.userEmail}>
                                          <span>{trade.userEmail.split('@')[0]}</span>
                                        </Tooltip>
                                      </TableCell>
                                      <TableCell>{trade.session}</TableCell>
                                      <TableCell>
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                          {trade.tpMilestonesReached && trade.tpMilestonesReached.map((milestone, idx) => (
                                            <Chip 
                                              key={idx}
                                              label={milestone}
                                              color={milestone === selectedMilestone ? "warning" : "primary"}
                                              size="small"
                                              variant={milestone === selectedMilestone ? "filled" : "outlined"}
                                            />
                                          ))}
                                        </Box>
                                      </TableCell>
                                      <TableCell>{trade.stopReason || 'לא צוין'}</TableCell>
                                      <TableCell>{formatDuration(trade.duration)}</TableCell>
                                      <TableCell>
                                        <Button 
                                          size="small" 
                                          variant="outlined"
                                          startIcon={<InfoIcon />}
                                          onClick={() => handleDetailClick(trade)}
                                        >
                                          פרטים
                                        </Button>
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </TableContainer>
                          </Paper>
                        </Box>
                      )}
                    </>
                  )}
                </>
              )}
  
              {/* Pestaña 5: תקופות ורצפים */}
              {activeTab === 4 && (
                <Grid container spacing={3}>
                  {/* כרטיס תקופת המסחר */}
                  <Grid item xs={12}>
                    <Paper sx={styles.card}>
                      <Box sx={styles.header}>
                        <Typography variant="h6" fontWeight="bold">
                          <DateRangeIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                          תקופת המסחר הכוללת
                        </Typography>
                      </Box>
                      <Box sx={{ p: 3 }}>
                        {tradingPeriods ? (
                          <Grid container spacing={3}>
                            <Grid item xs={12} md={6}>
                              <Box sx={{ 
                                bgcolor: alpha(theme.palette.primary.main, 0.05), 
                                p: 2, 
                                borderRadius: 2,
                                height: '100%'
                              }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                  <CalendarTodayIcon color="primary" sx={{ mr: 2 }} />
                                  <Typography variant="h6" fontWeight="bold">טווח תאריכים</Typography>
                                </Box>
                                <Box sx={{ p: 1 }}>
                                  <Grid container spacing={2}>
                                    <Grid item xs={12}>
                                      <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center' }}>
                                        <Typography component="span" fontWeight="bold" mr={1}>עסקה ראשונה:</Typography>
                                        {safeFormatDate(tradingPeriods.firstDate, 'dd/MM/yyyy')}
                                      </Typography>
                                    </Grid>
                                    <Grid item xs={12}>
                                      <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center' }}>
                                        <Typography component="span" fontWeight="bold" mr={1}>עסקה אחרונה:</Typography>
                                        {safeFormatDate(tradingPeriods.lastDate, 'dd/MM/yyyy')}
                                      </Typography>
                                    </Grid>
                                    <Grid item xs={12}>
                                      <Divider sx={{ my: 1 }} />
                                    </Grid>
                                    <Grid item xs={12}>
                                      <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center' }}>
                                        <Typography component="span" fontWeight="bold" mr={1}>תקופה כוללת:</Typography>
                                        {tradingPeriods.daysDiff} ימים ({Math.floor(tradingPeriods.monthsDiff)} חודשים ו-{tradingPeriods.daysDiff % 30} ימים)
                                      </Typography>
                                    </Grid>
                                    <Grid item xs={12}>
                                      <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center' }}>
                                        <Typography component="span" fontWeight="bold" mr={1}>מספר חודשי מסחר:</Typography>
                                        {tradingPeriods.months.length} חודשים
                                      </Typography>
                                    </Grid>
                                  </Grid>
                                </Box>
                              </Box>
                            </Grid>
  
                            <Grid item xs={12} md={6}>
                              <Box sx={{ 
                                bgcolor: alpha(theme.palette.info.main, 0.05), 
                                p: 2, 
                                borderRadius: 2,
                                height: '100%'
                              }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                  <AccessTimeIcon color="info" sx={{ mr: 2 }} />
                                  <Typography variant="h6" fontWeight="bold">חודשי המסחר</Typography>
                                </Box>
                                <Box sx={{ overflowY: 'auto', maxHeight: 150 }}>
                                  <Grid container spacing={1}>
                                    {tradingPeriods.monthsArray.map((monthData, index) => (
                                      <Grid item xs={4} sm={3} md={4} lg={3} key={monthData.month}>
                                        <Chip 
                                          label={monthData.name} 
                                          color="primary" 
                                          variant="outlined"
                                          sx={{ width: '100%' }}
                                        />
                                      </Grid>
                                    ))}
                                  </Grid>
                                </Box>
                              </Box>
                            </Grid>
                          </Grid>
                        ) : (
                          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 3 }}>
                            <Typography variant="body1" color="text.secondary">
                              אין מספיק נתונים לחישוב תקופת המסחר
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </Paper>
                  </Grid>
  
                  {/* גרף פעילות מסחר */}
                  <Grid item xs={12}>
                    <Paper sx={styles.card}>
                      <Box sx={styles.header}>
                        <Typography variant="h6" fontWeight="bold">
                          <ShowChartIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                          פעילות מסחר לאורך זמן
                        </Typography>
                      </Box>
                      <Box sx={{ p: 2, height: 400 }}>
                        <ResponsiveContainer width="100%" height="100%">
                          <AreaChart
                            data={tradingPeriods?.monthsArray.map(month => {
                              const monthTrades = filteredTrades.filter(trade => {
                                if (!isValid(trade.parsedDate)) return false;
                                return safeFormatDate(trade.parsedDate, 'yyyy-MM') === month.month;
                              });
                              
                              const wins = monthTrades.filter(t => t.outcome === 'Take Profit').length;
                              const losses = monthTrades.filter(t => t.outcome === 'Stop Loss').length;
                              
                              return {
                                month: month.name,
                                total: monthTrades.length,
                                wins,
                                losses,
                                winRate: monthTrades.length > 0 ? (wins / monthTrades.length) * 100 : 0
                              };
                            }) || []}
                            margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
                          >
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="month" />
                            <YAxis />
                            <RechartsTooltip />
                            <Legend />
                            <Area type="monotone" dataKey="total" name="סה״כ עסקאות" stroke={theme.palette.primary.main} fill={alpha(theme.palette.primary.main, 0.2)} />
                            <Area type="monotone" dataKey="wins" name="הצלחות" stroke={theme.palette.success.main} fill={alpha(theme.palette.success.main, 0.2)} />
                            <Area type="monotone" dataKey="losses" name="כשלונות" stroke={theme.palette.error.main} fill={alpha(theme.palette.error.main, 0.2)} />
                          </AreaChart>
                        </ResponsiveContainer>
                      </Box>
                    </Paper>
                  </Grid>
  
                  {/* כרטיסי רצפים */}
                  <Grid item xs={12} md={6}>
                    <Paper sx={styles.card}>
                      <Box sx={styles.header}>
                        <Typography variant="h6" fontWeight="bold">
                          <LocalFireDepartmentIcon sx={{ verticalAlign: 'middle', mr: 1, color: theme.palette.success.main }} />
                          רצף ניצחונות הארוך ביותר
                        </Typography>
                      </Box>
                      <Box sx={{ p: 3 }}>
                      {(selectedMilestone === 'all' ? streakData : simulatedStreakData).winStreaks.length > 0 ? (
                          <>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                              <Box>
                                <Typography variant="h4" color="success.main" fontWeight="bold">
                                  {(selectedMilestone === 'all' ? streakData : simulatedStreakData).winStreaks[0].length} עסקאות רווחיות ברצף
                                </Typography>
                                <Typography variant="body2" sx={{ mt: 1 }}>
                                  בתקופה: {
                                    isValid((selectedMilestone === 'all' ? streakData : simulatedStreakData).winStreaks[0][0].parsedDate) && 
                                    isValid((selectedMilestone === 'all' ? streakData : simulatedStreakData).winStreaks[0][(selectedMilestone === 'all' ? streakData : simulatedStreakData).winStreaks[0].length-1].parsedDate) ? 
                                    `${safeFormatDate((selectedMilestone === 'all' ? streakData : simulatedStreakData).winStreaks[0][0].parsedDate, 'dd/MM/yyyy')} — ${safeFormatDate((selectedMilestone === 'all' ? streakData : simulatedStreakData).winStreaks[0][(selectedMilestone === 'all' ? streakData : simulatedStreakData).winStreaks[0].length-1].parsedDate, 'dd/MM/yyyy')}` :
                                    'תאריכים לא זמינים'
                                  }
                                </Typography>
                              </Box>
                              <Box>
                                <Chip 
                                  label={`${streakData.winStreaks.length} רצפים`}
                                  color="success"
                                  variant="outlined"
                                />
                              </Box>
                            </Box>
  
                            <Divider sx={{ my: 2 }} />
  
                            <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
                              פירוט העסקאות ברצף הארוך ביותר
                            </Typography>
  
                            <TableContainer sx={{ maxHeight: 200 }}>
                              <Table size="small" stickyHeader>
                                <TableHead>
                                  <TableRow>
                                    <TableCell sx={styles.tableHeaderCell}>מס'</TableCell>
                                    <TableCell sx={styles.tableHeaderCell}>תאריך</TableCell>
                                    <TableCell sx={styles.tableHeaderCell}>סימבול</TableCell>
                                    <TableCell sx={styles.tableHeaderCell}>משתמש</TableCell>
                                    <TableCell sx={styles.tableHeaderCell}>יעד TP</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {(selectedMilestone === 'all' ? streakData : simulatedStreakData).winStreaks[0].map((trade, index) => (
                                    <TableRow key={trade.id} hover>
                                      <TableCell>{index + 1}</TableCell>
                                      <TableCell>{trade.date}</TableCell>
                                      <TableCell>{trade.symbol}</TableCell>
                                      <TableCell>
                                        <Tooltip title={trade.userEmail}>
                                          <span>{trade.userEmail?.split('@')[0] || 'Unknown'}</span>
                                        </Tooltip>
                                      </TableCell>
                                      <TableCell>{trade.tpMilestoneExit || '-'}</TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </TableContainer>
                          </>
                        ) : (
                          <Box sx={{ p: 3, textAlign: 'center' }}>
                            <Typography variant="body1" color="text.secondary">
                              לא נמצאו רצפי ניצחונות
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </Paper>
                  </Grid>
  
                  <Grid item xs={12} md={6}>
                    <Paper sx={styles.card}>
                      <Box sx={styles.header}>
                        <Typography variant="h6" fontWeight="bold">
                          <WarningIcon sx={{ verticalAlign: 'middle', mr: 1, color: theme.palette.error.main }} />
                          רצף הפסדים הארוך ביותר
                        </Typography>
                      </Box>
                      <Box sx={{ p: 3 }}>
                      {(selectedMilestone === 'all' ? streakData : simulatedStreakData).lossStreaks.length > 0 ? (
                          <>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                              <Box>
                                <Typography variant="h4" color="error.main" fontWeight="bold">
                                  {(selectedMilestone === 'all' ? streakData : simulatedStreakData).lossStreaks[0].length} עסקאות מפסידות ברצף
                                </Typography>
                                <Typography variant="body2" sx={{ mt: 1 }}>
                                  בתקופה: {
                                    isValid((selectedMilestone === 'all' ? streakData : simulatedStreakData).lossStreaks[0][0].parsedDate) && 
                                    isValid((selectedMilestone === 'all' ? streakData : simulatedStreakData).lossStreaks[0][(selectedMilestone === 'all' ? streakData : simulatedStreakData).lossStreaks[0].length-1].parsedDate) ? 
                                    `${safeFormatDate((selectedMilestone === 'all' ? streakData : simulatedStreakData).lossStreaks[0][0].parsedDate, 'dd/MM/yyyy')} — ${safeFormatDate((selectedMilestone === 'all' ? streakData : simulatedStreakData).lossStreaks[0][(selectedMilestone === 'all' ? streakData : simulatedStreakData).lossStreaks[0].length-1].parsedDate, 'dd/MM/yyyy')}` :
                                    'תאריכים לא זמינים'
                                  }
                                </Typography>
                              </Box>
                              <Box>
                                <Chip 
                                  label={`${streakData.lossStreaks.length} רצפים`}
                                  color="error"
                                  variant="outlined"
                                />
                              </Box>
                            </Box>
  
                            <Divider sx={{ my: 2 }} />
  
                            <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
                              פירוט העסקאות ברצף הארוך ביותר
                            </Typography>
  
                            <TableContainer sx={{ maxHeight: 200 }}>
                              <Table size="small" stickyHeader>
                                <TableHead>
                                  <TableRow>
                                    <TableCell sx={styles.tableHeaderCell}>מס'</TableCell>
                                    <TableCell sx={styles.tableHeaderCell}>תאריך</TableCell>
                                    <TableCell sx={styles.tableHeaderCell}>סימבול</TableCell>
                                    <TableCell sx={styles.tableHeaderCell}>משתמש</TableCell>
                                    <TableCell sx={styles.tableHeaderCell}>סיבת סטופ</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {(selectedMilestone === 'all' ? streakData : simulatedStreakData).lossStreaks[0].map((trade, index) => (
                                    <TableRow key={trade.id} hover>
                                      <TableCell>{index + 1}</TableCell>
                                      <TableCell>{trade.date}</TableCell>
                                      <TableCell>{trade.symbol}</TableCell>
                                      <TableCell>
                                        <Tooltip title={trade.userEmail}>
                                          <span>{trade.userEmail?.split('@')[0] || 'Unknown'}</span>
                                        </Tooltip>
                                      </TableCell>
                                      <TableCell>{trade.stopReason || '-'}</TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </TableContainer>
                          </>
                        ) : (
                          <Box sx={{ p: 3, textAlign: 'center' }}>
                            <Typography variant="body1" color="text.secondary">
                              לא נמצאו רצפי הפסדים
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </Paper>
                  </Grid>
  
                  {/* השוואת רצפים וסטטיסטיקה */}
                  <Grid item xs={12}>
                    <Paper sx={styles.card}>
                      <Box sx={styles.header}>
                        <Typography variant="h6" fontWeight="bold">
                          <RepeatIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                          ניתוח רצפים נוספים
                        </Typography>
                      </Box>
                      <Box sx={{ p: 2 }}>
                        <Grid container spacing={2}>
                          <Grid item xs={12} md={6}>
                            <Box sx={{ 
                              height: 400,
                              display: 'flex',
                              flexDirection: 'column'
                            }}>
                              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                                גודל הרצפים
                              </Typography>
                              <Box sx={{ flex: 1 }}>
                                <ResponsiveContainer width="100%" height="100%">
                                  <BarChart
                                    data={[...Array(5)].map((_, i) => {
                                      const winStreakCount = streakData.winStreaks.filter(streak => streak.length === i + 2).length;
                                      const lossStreakCount = streakData.lossStreaks.filter(streak => streak.length === i + 2).length;
                                      
                                      return {
                                        length: i + 2,
                                        wins: winStreakCount,
                                        losses: lossStreakCount
                                      };
                                    })}
                                    margin={{ top: 10, right: 30, left: 0, bottom: 50 }}
                                  >
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis 
                                      dataKey="length" 
                                      label={{ 
                                        value: 'גודל הרצף (מספר עסקאות)', 
                                        position: 'insideBottom', 
                                        offset: -10 
                                      }}
                                    />
                                    <YAxis label={{ value: 'מספר הרצפים', angle: -90, position: 'insideLeft' }} />
                                    <RechartsTooltip />
                                    <Legend />
                                    <Bar dataKey="wins" name="רצפי ניצחונות" fill={theme.palette.success.main} />
                                    <Bar dataKey="losses" name="רצפי הפסדים" fill={theme.palette.error.main} />
                                  </BarChart>
                                </ResponsiveContainer>
                              </Box>
                            </Box>
                          </Grid>
  
                          <Grid item xs={12} md={6}>
                            <Box sx={{ 
                              height: 400,
                              bgcolor: alpha(theme.palette.grey[100], 0.5),
                              p: 2,
                              borderRadius: 2
                            }}>
                              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                                ציר זמן רצפים
                              </Typography>
                              
                              <Box sx={{ overflow: 'auto', maxHeight: 350 }}>
                                <Timeline position="alternate">
                                  {[...streakData.winStreaks, ...streakData.lossStreaks]
                                    .filter(streak => streak.length >= 3) // רק רצפים משמעותיים
                                    .sort((a, b) => {
                                      // מיון לפי תאריך הרצף הראשון
                                      if (!isValid(a[0].parsedDate) || !isValid(b[0].parsedDate)) return 0;
                                      return compareAsc(a[0].parsedDate, b[0].parsedDate);
                                    })
                                    .slice(0, 6) // הגבלה לכמות סבירה
                                    .map((streak, index) => {
                                      const isWinStreak = streak[0].outcome === 'Take Profit';
                                      const startDate = isValid(streak[0].parsedDate) ? 
                                        safeFormatDate(streak[0].parsedDate, 'dd/MM/yy') : 'תאריך לא ידוע';
                                      const endDate = isValid(streak[streak.length-1].parsedDate) ? 
                                        safeFormatDate(streak[streak.length-1].parsedDate, 'dd/MM/yy') : 'תאריך לא ידוע';
                                      
                                      return (
                                        <TimelineItem key={index}>
                                          <TimelineOppositeContent color="text.secondary">
                                            {startDate} - {endDate}
                                          </TimelineOppositeContent>
                                          <TimelineSeparator>
                                            <TimelineDot color={isWinStreak ? "success" : "error"}>
                                              {isWinStreak ? <WhatshotIcon /> : <TrendingDownIcon />}
                                            </TimelineDot>
                                            <TimelineConnector />
                                          </TimelineSeparator>
                                          <TimelineContent>
                                            <Typography variant="subtitle2" fontWeight="bold" component="span">
                                              {streak.length} {isWinStreak ? 'ניצחונות' : 'הפסדים'} ברצף
                                            </Typography>
                                            <Typography variant="body2">
                                              {isWinStreak ? 
                                                `רצף TP עם סימבולים: ${[...new Set(streak.map(t => t.symbol))].join(', ')}` :
                                                `רצף SL עם סימבולים: ${[...new Set(streak.map(t => t.symbol))].join(', ')}`
                                              }
                                            </Typography>
                                          </TimelineContent>
                                        </TimelineItem>
                                      );
                                    })
                                  }
                                </Timeline>
                              </Box>
                            </Box>
                          </Grid>
                        </Grid>
                      </Box>
                    </Paper>
                  </Grid>
                </Grid>
              )}
  
              {/* Pestaña 6: ניתוח לפי משתמש (New tab for user analysis) */}
              {activeTab === 5 && (
                <>
                  {selectedMilestone === 'all' ? (
                    <Alert severity="info" sx={{ mb: 3 }}>
                      נא לבחור אסטרטגיית יציאה כדי לראות את ניתוח המשתמשים
                    </Alert>
                  ) : (
                    <Grid container spacing={3}>
                      {/* User performance comparison */}
                      <Grid item xs={12}>
                        <Paper sx={styles.card}>
                          <Box sx={styles.header}>
                            <Typography variant="h6" fontWeight="bold">
                              <PeopleIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                              השוואת ביצועי משתמשים עם יציאה ב-{selectedMilestone}
                            </Typography>
                          </Box>
                          <Box sx={{ p: 2, height: 400 }}>
                            <ResponsiveContainer width="100%" height="100%">
                              <BarChart
                                data={selectedSimulation.userAnalysis}
                                margin={{ top: 20, right: 30, left: 30, bottom: 80 }}
                              >
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis 
                                  dataKey="userEmail"
                                  angle={-45}
                                  textAnchor="end"
                                  height={80}
                                  tick={{ fontSize: 12 }}
                                />
                                <YAxis yAxisId="left" orientation="left" />
                                <YAxis yAxisId="right" orientation="right" domain={[0, 100]} />
                                <RechartsTooltip />
                                <Legend />
                                <Bar yAxisId="left" dataKey="totalR" name="סה״כ R" fill={theme.palette.primary.main} />
                                <Bar yAxisId="right" dataKey="winRate" name="אחוז הצלחה" fill={theme.palette.info.main} />
                              </BarChart>
                            </ResponsiveContainer>
                          </Box>
                        </Paper>
                      </Grid>
  
                      {/* User detailed stats */}
                      <Grid item xs={12}>
                        <Paper sx={styles.tableContainer}>
                          <Box 
                            sx={{ 
                              py: 1.5, 
                              px: 2, 
                              bgcolor: alpha(theme.palette.primary.main, 0.1), 
                              display: 'flex', 
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`
                            }}
                          >
                            <Typography variant="h6" fontWeight="bold" color="primary.main">
                              ניתוח ביצועים לפי משתמש
                            </Typography>
                          </Box>
                          <TableContainer>
                            <Table>
                              <TableHead>
                                <TableRow>
                                  <TableCell sx={styles.tableHeaderCell}>משתמש</TableCell>
                                  <TableCell sx={styles.tableHeaderCell}>מס' עסקאות</TableCell>
                                  <TableCell sx={styles.tableHeaderCell}>הצלחות</TableCell>
                                  <TableCell sx={styles.tableHeaderCell}>כשלונות</TableCell>
                                  <TableCell sx={styles.tableHeaderCell}>אחוז הצלחה</TableCell>
                                  <TableCell sx={styles.tableHeaderCell}>R נוכחי</TableCell>
                                  <TableCell sx={styles.tableHeaderCell}>R סימולציה</TableCell>
                                  <TableCell sx={styles.tableHeaderCell}>שינוי</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {selectedSimulation.userAnalysis.map((user) => {
                                  // Get current real stats for this user
                                  const currentUserStats = baselineStats.userAnalysis?.find(u => u.userEmail === user.userEmail) || {
                                    totalR: 0,
                                    winRate: 0
                                  };
  
                                  const rDifference = user.totalR - currentUserStats.totalR;
                                  
                                  return (
                                    <TableRow 
                                      key={user.userEmail}
                                      hover
                                      sx={{
                                        bgcolor: user.totalR > currentUserStats.totalR ? 
                                          alpha(theme.palette.success.main, 0.05) : 
                                          user.totalR < currentUserStats.totalR ? 
                                            alpha(theme.palette.error.main, 0.05) : 
                                            'transparent'
                                      }}
                                    >
                                      <TableCell>
                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                          <PersonIcon sx={{ mr: 1, color: 'primary.main' }} />
                                          <Tooltip title={user.userEmail}>
                                            <Typography 
                                              variant="body2" 
                                              sx={{
                                                maxWidth: '200px',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap'
                                              }}
                                            >
                                              {user.userEmail}
                                            </Typography>
                                          </Tooltip>
                                        </Box>
                                      </TableCell>
                                      <TableCell>{user.totalTrades}</TableCell>
                                      <TableCell>{user.winCount}</TableCell>
                                      <TableCell>{user.lossCount}</TableCell>
                                      <TableCell 
                                        sx={{ fontWeight: 'bold' }}
                                      >
                                        {user.winRate.toFixed(1)}%
                                      </TableCell>
                                      <TableCell>{currentUserStats.totalR.toFixed(2)}R</TableCell>
                                      <TableCell 
                                        sx={{ 
                                          color: user.totalR >= 0 ? theme.palette.success.main : theme.palette.error.main,
                                          fontWeight: 'bold' 
                                        }}
                                      >
                                        {user.totalR.toFixed(2)}R
                                      </TableCell>
                                      <TableCell>
                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                          {rDifference > 0 ? (
                                            <>
                                              <ArrowUpwardIcon fontSize="small" color="success" />
                                              <Typography variant="body2" color="success.main" fontWeight="bold">
                                                +{rDifference.toFixed(2)}R
                                              </Typography>
                                            </>
                                          ) : rDifference < 0 ? (
                                            <>
                                              <ArrowDownwardIcon fontSize="small" color="error" />
                                              <Typography variant="body2" color="error.main" fontWeight="bold">
                                                {rDifference.toFixed(2)}R
                                              </Typography>
                                            </>
                                          ) : (
                                            <Typography variant="body2" color="text.secondary">
                                              ללא שינוי
                                            </Typography>
                                          )}
                                        </Box>
                                      </TableCell>
                                    </TableRow>
                                  );
                                })}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </Paper>
                      </Grid>
  
                      {/* User vs user comparison */}
                      {selectedSimulation.userAnalysis.length >= 2 && (
                        <Grid item xs={12}>
                          <Paper sx={styles.card}>
                            <Box sx={styles.header}>
                              <Typography variant="h6" fontWeight="bold">
                                <CompareIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                                השוואה בין משתמשים עם סימולציית יציאה ב-{selectedMilestone}
                              </Typography>
                            </Box>
                            <Box sx={{ p: 2, height: 400 }}>
                              <ResponsiveContainer width="100%" height="100%">
                                <RadarChart 
                                  cx="50%" 
                                  cy="50%" 
                                  outerRadius="80%" 
                                  data={[
                                    { subject: 'Win Rate', 
                                      ...Object.fromEntries(
                                        selectedSimulation.userAnalysis
                                          .slice(0, 5) // Limit to first 5 users for better visualization
                                          .map((user, idx) => [`user${idx}`, user.winRate])
                                      )
                                    },
                                    { subject: 'Avg R', 
                                      ...Object.fromEntries(
                                        selectedSimulation.userAnalysis
                                          .slice(0, 5)
                                          .map((user, idx) => [`user${idx}`, user.totalTrades > 0 ? user.totalR / user.totalTrades * 20 : 0])
                                      )
                                    },
                                    { subject: 'Trade Count', 
                                      ...Object.fromEntries(
                                        selectedSimulation.userAnalysis
                                          .slice(0, 5)
                                          .map((user, idx) => {
                                            const maxTrades = Math.max(...selectedSimulation.userAnalysis.map(u => u.totalTrades));
                                            return [`user${idx}`, user.totalTrades / maxTrades * 100];
                                          })
                                      )
                                    },
                                    { subject: 'Total R', 
                                      ...Object.fromEntries(
                                        selectedSimulation.userAnalysis
                                          .slice(0, 5)
                                          .map((user, idx) => {
                                            const maxR = Math.max(...selectedSimulation.userAnalysis
                                              .filter(u => u.totalR > 0)
                                              .map(u => u.totalR));
                                            return [`user${idx}`, user.totalR > 0 ? user.totalR / maxR * 100 : 0];
                                          })
                                      )
                                    }
                                  ]}
                                >
                                  <PolarGrid />
                                  <PolarAngleAxis dataKey="subject" />
                                  <PolarRadiusAxis angle={30} domain={[0, 100]} />
                                  
                                  {selectedSimulation.userAnalysis.slice(0, 5).map((user, idx) => (
                                    <Radar 
                                      key={user.userEmail}
                                      name={user.userEmail.split('@')[0]} 
                                      dataKey={`user${idx}`} 
                                      stroke={COLORS[idx % COLORS.length]} 
                                      fill={COLORS[idx % COLORS.length]} 
                                      fillOpacity={0.6} 
                                    />
                                  ))}
  
                                  <Legend />
                                </RadarChart>
                              </ResponsiveContainer>
                            </Box>
                          </Paper>
                        </Grid>
                      )}
                    </Grid>
                  )}
                </>
              )}
  
              {/* Información de pie */}
              <Box sx={{ mt: 4, mb: 2, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  סימולטור אסטרטגיית יציאה מבוסס על נתוני העסקאות שתיעדת במערכת, כולל מדדי TP Milestone שהושגו
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  הסימולטור מציג מה היו התוצאות אילו היית יוצא באופן עקבי בנקודת יציאה מסוימת
                </Typography>
                <Typography variant="body2" color="text.secondary" fontWeight="bold">
                </Typography>
              </Box>
            </>
          )}
        </Box>
      </Box>
    );
  };
  
  // Componente personalizado para el contenido del Treemap
  const CustomizedContent = (props) => {
    const { root, depth, x, y, width, height, index, colors, name, value } = props;
  
    return (
      <g>
        <rect
          x={x}
          y={y}
          width={width}
          height={height}
          style={{
            fill: depth < 2 ? colors(index) : 'transparent',
            stroke: '#fff',
            strokeWidth: 2,
            strokeOpacity: 1,
          }}
        />
        {depth === 1 && (
          <text
            x={x + width / 2}
            y={y + height / 2 + 7}
            textAnchor="middle"
            fill="#fff"
            fontSize={14}
            fontWeight="bold"
          >
            {name}
          </text>
        )}
        {depth === 1 && (
          <text
            x={x + width / 2}
            y={y + height / 2 - 7}
            textAnchor="middle"
            fill="#fff"
            fontSize={16}
            fontWeight="bold"
          >
            {root.children[index].children.length}
          </text>
        )}
      </g>
    );
  };
  
  export default TPExitSimulator;